import * as i0 from '@angular/core';
import { Injectable, Component, ViewChild, Input, Directive, ContentChild, forwardRef, HostBinding, EnvironmentInjector, ViewContainerRef, HostListener, createComponent, inject, TemplateRef, Inject, ElementRef, ContentChildren, NgModule, Pipe } from '@angular/core';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule, Validators, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@angular/router';
import { NavigationStart, RouterModule } from '@angular/router';
import { BehaviorSubject, map, shareReplay, forkJoin, of, Subject, startWith, combineLatest, Observable, tap, defaultIfEmpty } from 'rxjs';
import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2$1 from '@angular/platform-browser';
import * as i1$2 from '@angular/common/http';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormGroup, FormControl } from '@ngneat/reactive-forms';
import { catchError } from 'rxjs/operators';

// Creates a Unique Identifier
// To be used for dynamic IDs, etc
function createUniqueIdentifier() {
    const numbers = window.crypto.getRandomValues(new Uint32Array(1))[0].toString();
    const letters = window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36).substring(1);
    const uniqueIdentifier = numbers + letters;
    return uniqueIdentifier;
}

class ValidationErrorService {
    constructor(router) {
        this.router = router;
        this._form$ = new BehaviorSubject(undefined);
        this.validationErrorList = [];
        this._validationErrors$ = new BehaviorSubject([]);
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.reset();
            }
        });
    }
    set form(val) {
        this._form$.next(val);
    }
    validationErrors$() {
        return this._validationErrors$.asObservable();
    }
    addError(error) {
        this.validationErrorList = [...this.validationErrorList, error];
        this._validationErrors$.next(this.validationErrorList);
    }
    removeError(error) {
        this.validationErrorList = this.validationErrorList.filter(element => element.message !== error.message);
        this._validationErrors$.next(this.validationErrorList);
    }
    reset() {
        this.validationErrorList = [];
        this._validationErrors$.next(this.validationErrorList);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ValidationErrorService, deps: [{ token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ValidationErrorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ValidationErrorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.Router }] });

class ValidationErrorMessageComponent {
    constructor(validationError) {
        this.validationError = validationError;
    }
    ngAfterViewInit() {
        if (!this.error) {
            return;
        }
        const errorEl = this.error.nativeElement;
        this.validationError.addError({ message: errorEl.innerText, element: errorEl });
    }
    ngOnDestroy() {
        if (!this.error) {
            return;
        }
        const errorEl = this.error.nativeElement;
        this.validationError.removeError({ message: errorEl.innerText, element: errorEl });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ValidationErrorMessageComponent, deps: [{ token: ValidationErrorService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: ValidationErrorMessageComponent, isStandalone: true, selector: "sl-validation-error-message", inputs: { container: "container" }, viewQueries: [{ propertyName: "error", first: true, predicate: ["error"], descendants: true }], ngImport: i0, template: "<span class=\"validation-error-message\" #error><ng-content></ng-content></span>", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ValidationErrorMessageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-validation-error-message', standalone: true, template: "<span class=\"validation-error-message\" #error><ng-content></ng-content></span>" }]
        }], ctorParameters: () => [{ type: ValidationErrorService }], propDecorators: { error: [{
                type: ViewChild,
                args: ['error']
            }], container: [{
                type: Input
            }] } });

/**
 * Provides the common parts of the implementation of ControlValueAccessor.
 *
 * @export
 * @abstract
 * @class CVABase
 * @implements {ControlValueAccessor}
 * @template T
 */
class CVABase {
    constructor() {
        this.onChange = new Array();
        this.onTouched = new Array();
        this.stripHtmlTags = true;
        this.identifier = createUniqueIdentifier();
    }
    registerOnChange(fn) {
        this.onChange.push(fn);
    }
    registerOnTouched(fn) {
        this.onTouched.push(fn);
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    get value() {
        return this._value;
    }
    set value(value) {
        if (typeof value == 'string' && this.stripHtmlTags) {
            /* eslint-disable @typescript-eslint/no-unsafe-assignment */
            this._value = this.sanitize(value);
        }
        else {
            this._value = value;
        }
        this.onChange.forEach(f => f(this._value));
    }
    /**
     * Pushes a new value into the control.
     *
     * @param value
     */
    writeValue(value) {
        this._value = value;
    }
    /**
     * Sanitizes a given string by removing angle brackets.
     *
     * @param text the text to sanitize
     */
    sanitize(text) {
        return text.replace(/[<>]/g, '');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: CVABase, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.1.1", type: CVABase, isStandalone: true, queries: [{ propertyName: "errorMessage", first: true, predicate: ValidationErrorMessageComponent, descendants: true }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: CVABase, decorators: [{
            type: Directive
        }], propDecorators: { errorMessage: [{
                type: ContentChild,
                args: [ValidationErrorMessageComponent]
            }] } });

class SlTimeComponent extends CVABase {
    constructor() {
        super(...arguments);
        this.dateOnly = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlTimeComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlTimeComponent, isStandalone: true, selector: "sl-time", inputs: { dateOnly: "dateOnly", name: "name", min: "min", max: "max", label: "label" }, host: { properties: { "class.hasError": "this.errorMessage" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SlTimeComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"sl-time\">\n  <label *ngIf=\"label\" [for]=\"identifier\">{{ label }}</label>\n  <input\n    [id]=\"identifier\"\n    [type]=\"'time'\"\n    [name]=\"name\"\n    [min]=\"min\"\n    [max]=\"max\"\n    [(ngModel)]=\"value\"\n    [disabled]=\"disabled\"\n  />\n  <span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n    <ng-content select=\"sl-validation-error-message\"></ng-content>\n  </span>\n</div>", styles: [""], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlTimeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-time', imports: [CommonModule, FormsModule], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SlTimeComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"sl-time\">\n  <label *ngIf=\"label\" [for]=\"identifier\">{{ label }}</label>\n  <input\n    [id]=\"identifier\"\n    [type]=\"'time'\"\n    [name]=\"name\"\n    [min]=\"min\"\n    [max]=\"max\"\n    [(ngModel)]=\"value\"\n    [disabled]=\"disabled\"\n  />\n  <span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n    <ng-content select=\"sl-validation-error-message\"></ng-content>\n  </span>\n</div>" }]
        }], propDecorators: { errorMessage: [{
                type: HostBinding,
                args: ['class.hasError']
            }], dateOnly: [{
                type: Input
            }], name: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], label: [{
                type: Input
            }] } });

/**
 * Extends the base ControlValueAccessor implementation with common code for handling lists
 *
 * @export
 * @abstract
 * @class CVAListBase
 * @implements {ControlValueAccessor}
 * @template T
 */
class CVAListBase extends CVABase {
    constructor() {
        super(...arguments);
        this.selectionChanges$ = new BehaviorSubject([]);
        /**
         * The list of options.
         * This should be updated from the server as appropriate.
         *
         * @type {Array<T>}
         */
        this.options = [];
    }
    /**
     * The central value of the control
     */
    get list() {
        return super.value;
    }
    set list(list) {
        throw new Error('Cannot set list directly. Use add or remove methods instead.');
    }
    get value() {
        throw new Error('Cannot get value directly. Use list property instead.');
    }
    set value(value) {
        throw new Error('Cannot set value directly. Use list property instead.');
    }
    /**
     * Replaces the list in the control.
     *
     * @param list
     */
    writeValue(list) {
        const newList = list || [];
        this._value = newList;
        this.selectionChanges$.next(newList);
    }
    /**
     * Adds an item to the list and notifies all registered change handlers.
     *
     * @param item - The item to be added to the list.
     */
    add(item) {
        if (typeof item == 'string') {
            /* eslint-disable @typescript-eslint/no-unsafe-assignment */
            this._value.push(this.sanitize(item));
        }
        else {
            this._value.push(item);
        }
        this.selectionChanges$.next(this._value);
        this.onChange.forEach(f => f(this._value));
    }
    /**
     * Removes an item from the list and notifies all registered change handlers.
     *
     * @param item - The item to be removed from the list.
     */
    remove(item) {
        const index = this._value.findIndex(i => this.equals(i, item));
        if (index > -1) {
            this._value.splice(index, 1);
            this.selectionChanges$.next(this._value);
            this.onChange.forEach(f => f(this._value));
        }
    }
}

class SlCheckboxComponent extends CVABase {
    constructor(sanitizer) {
        super();
        this.sanitizer = sanitizer;
    }
    ngOnInit() {
        if (!this.label)
            return;
        this.label = this.sanitizer.bypassSecurityTrustHtml(this.label);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlCheckboxComponent, deps: [{ token: i2$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlCheckboxComponent, isStandalone: true, selector: "sl-checkbox", inputs: { name: "name", label: "label" }, host: { properties: { "class.hasError": "this.errorMessage" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SlCheckboxComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<label class=\"sl-checkbox\" [for]=\"identifier\">\n  <input\n    type=\"checkbox\"\n    [name]=\"name\"\n    [id]=\"identifier\"\n    [disabled]=\"disabled\"\n    [(ngModel)]=\"value\"\n  />\n  <span class=\"checkbox\">\n    <svg viewBox=\"0 0 10 10\" class=\"checkmark\">\n      <path d=\"M1.2,5.1 L3.5,7.5 L8.75,2.15\" />\n    </svg>\n  </span>\n  <span class=\"label\" [innerHtml]=\"label\"></span>\n  <ng-content *ngIf=\"!label\"></ng-content>\n</label>\n", styles: [""], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlCheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-checkbox', imports: [CommonModule, FormsModule], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SlCheckboxComponent),
                            multi: true,
                        },
                    ], template: "<label class=\"sl-checkbox\" [for]=\"identifier\">\n  <input\n    type=\"checkbox\"\n    [name]=\"name\"\n    [id]=\"identifier\"\n    [disabled]=\"disabled\"\n    [(ngModel)]=\"value\"\n  />\n  <span class=\"checkbox\">\n    <svg viewBox=\"0 0 10 10\" class=\"checkmark\">\n      <path d=\"M1.2,5.1 L3.5,7.5 L8.75,2.15\" />\n    </svg>\n  </span>\n  <span class=\"label\" [innerHtml]=\"label\"></span>\n  <ng-content *ngIf=\"!label\"></ng-content>\n</label>\n" }]
        }], ctorParameters: () => [{ type: i2$1.DomSanitizer }], propDecorators: { errorMessage: [{
                type: HostBinding,
                args: ['class.hasError']
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }] } });

class SlDatetimeComponent extends CVABase {
    constructor() {
        super(...arguments);
        this.dateOnly = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlDatetimeComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlDatetimeComponent, isStandalone: true, selector: "sl-datetime", inputs: { dateOnly: "dateOnly", name: "name", min: "min", max: "max", label: "label" }, host: { properties: { "class.hasError": "this.errorMessage" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SlDatetimeComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"sl-datetime\">\n  <label *ngIf=\"label\" [for]=\"identifier\">{{ label }}</label>\n  <input\n    [id]=\"identifier\"\n    [type]=\"dateOnly ? 'date' : 'datetime-local'\"\n    [name]=\"name\"\n    [min]=\"min\"\n    [max]=\"max\"\n    [(ngModel)]=\"value\"\n    [disabled]=\"disabled\"\n  />\n  <span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n    <ng-content select=\"sl-validation-error-message\"></ng-content>\n</span>\n</div>", styles: [""], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlDatetimeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-datetime', imports: [CommonModule, FormsModule], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SlDatetimeComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"sl-datetime\">\n  <label *ngIf=\"label\" [for]=\"identifier\">{{ label }}</label>\n  <input\n    [id]=\"identifier\"\n    [type]=\"dateOnly ? 'date' : 'datetime-local'\"\n    [name]=\"name\"\n    [min]=\"min\"\n    [max]=\"max\"\n    [(ngModel)]=\"value\"\n    [disabled]=\"disabled\"\n  />\n  <span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n    <ng-content select=\"sl-validation-error-message\"></ng-content>\n</span>\n</div>" }]
        }], propDecorators: { errorMessage: [{
                type: HostBinding,
                args: ['class.hasError']
            }], dateOnly: [{
                type: Input
            }], name: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], label: [{
                type: Input
            }] } });

class SlIconService {
    constructor() {
        this.iconMap = new Map();
    }
    hasIcon(path) {
        return this.iconMap.has(path);
    }
    getIcon(path) {
        return this.iconMap.get(path);
    }
    setIcon(path, svg) {
        this.iconMap.set(path, svg);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlIconService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlIconService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlIconService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * Bacon SL-Icon Component.
 *
 * @param {string} Icon - Takes the name of the svg file
 */
class SlIconComponent {
    constructor(httpClient, sanitizer, slIconService) {
        this.httpClient = httpClient;
        this.sanitizer = sanitizer;
        this.slIconService = slIconService;
        this.flip = false;
        this.colorFill = false;
        this.colorStroke = false;
        this.matchFontSize = false;
    }
    ngOnChanges() {
        if (!this.icon) {
            return;
        }
        if (this.path) {
            this.loadIcon(this.path);
        }
        else {
            this.loadIcon(`assets/svgs/`);
        }
    }
    //💡 IDEA: might be able to use this automagically add color="currentColor" to svgs rather than manually editing them ourselves
    checkForTitle(icon) {
        const htmlString = icon.toString();
        return htmlString.includes('title=');
    }
    insertTitle(icon) {
        const iconStr = icon.toString();
        const index = iconStr.indexOf('width');
        const title = this.title ? `title="${this.title}" ` : `title="" `;
        if (index !== -1) {
            let updatedIcon = iconStr.slice(0, index) + title + iconStr.slice(index);
            updatedIcon = this.extractSVGContent(updatedIcon);
            return this.sanitizer.bypassSecurityTrustHtml(updatedIcon);
        }
        else {
            return icon;
        }
    }
    extractSVGContent(htmlString) {
        const svgStartIndex = htmlString.indexOf('<svg');
        const svgEndIndex = htmlString.indexOf('</svg>');
        if (svgStartIndex !== -1 && svgEndIndex !== -1) {
            const svgContent = htmlString.slice(svgStartIndex, svgEndIndex + 6); // Adding 6 to include </svg>
            return svgContent;
        }
        else {
            return '';
        }
    }
    loadIcon(path) {
        path = path + `${this.icon}.svg`;
        let iconCached = this.slIconService.hasIcon(path);
        //check if the icon is cached
        if (iconCached) {
            this.slIconService.getIcon(path)?.subscribe(svg => {
                this.svg = svg;
            });
        }
        else {
            //if the icon is not in the map get it and add it in
            let svg$ = this.httpClient.get(path, { responseType: 'text' }).pipe(map((svg) => this.sanitizer.bypassSecurityTrustHtml(svg)), shareReplay(1));
            svg$.subscribe(icon => {
                if (!this.checkForTitle(icon)) {
                    this.svg = this.insertTitle(icon);
                }
                else {
                    this.svg = icon;
                }
            });
            this.slIconService.setIcon(path, svg$);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlIconComponent, deps: [{ token: i1$2.HttpClient }, { token: i2$1.DomSanitizer }, { token: SlIconService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlIconComponent, isStandalone: true, selector: "sl-icon", inputs: { icon: "icon", path: "path", title: "title", flip: "flip", colorFill: "colorFill", colorStroke: "colorStroke", matchFontSize: "matchFontSize" }, host: { properties: { "class.flip": "this.flip", "class.fill": "this.colorFill", "class.stroke": "this.colorStroke", "class.font": "this.matchFontSize" } }, usesOnChanges: true, ngImport: i0, template: "<div [innerHTML]=\"svg\"></div>", styles: [":host{display:inline-block;vertical-align:bottom;position:relative;height:min-content}:host div{height:100%;width:auto}:host::ng-deep svg{width:auto!important;height:100%!important;vertical-align:middle}:host.flip::ng-deep svg{transform:scaleX(-1)}:host.fill::ng-deep svg{fill:currentColor}:host.stroke::ng-deep svg{stroke:currentColor}:host.font::ng-deep svg{width:auto!important;height:1em!important}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-icon', imports: [CommonModule], template: "<div [innerHTML]=\"svg\"></div>", styles: [":host{display:inline-block;vertical-align:bottom;position:relative;height:min-content}:host div{height:100%;width:auto}:host::ng-deep svg{width:auto!important;height:100%!important;vertical-align:middle}:host.flip::ng-deep svg{transform:scaleX(-1)}:host.fill::ng-deep svg{fill:currentColor}:host.stroke::ng-deep svg{stroke:currentColor}:host.font::ng-deep svg{width:auto!important;height:1em!important}\n"] }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: i2$1.DomSanitizer }, { type: SlIconService }], propDecorators: { icon: [{
                type: Input
            }], path: [{
                type: Input
            }], title: [{
                type: Input
            }], flip: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.flip']
            }], colorFill: [{
                type: HostBinding,
                args: ['class.fill']
            }, {
                type: Input
            }], colorStroke: [{
                type: HostBinding,
                args: ['class.stroke']
            }, {
                type: Input
            }], matchFontSize: [{
                type: HostBinding,
                args: ['class.font']
            }, {
                type: Input
            }] } });

class ImpersonateService {
    constructor(router) {
        this.router = router;
        this.luidRegex = new RegExp(/^L\d{8}$/);
        this.integersRegex = new RegExp(/^\d+$/);
        this.storageKey = 'impersonateHistory';
    }
    setUserService(userService) {
        this.userService = userService;
    }
    setHistoryKey(key) {
        this.storageKey = key;
    }
    getUserService() {
        return this.userService;
    }
    loadObjects(key) {
        const storedData = localStorage.getItem(key);
        return storedData ? JSON.parse(storedData) : [];
    }
    saveObjects(key, objects) {
        localStorage.setItem(key, JSON.stringify(objects));
    }
    deleteObjects(key) {
        localStorage.removeItem(key);
    }
    addObject(key, newObject) {
        let objects = this.loadObjects(key);
        // Check for duplicates based on the unique property (id)
        if (!objects.some(obj => obj.id === newObject.id)) {
            // Add the new object to the array
            objects.push(newObject);
            // Check if the length exceeds 10 and remove the oldest object if necessary
            if (objects.length > 10) {
                objects.shift(); // Remove the first (oldest) object
            }
            // Save the updated array to localStorage
            this.saveObjects(key, objects);
        }
        else {
            return;
        }
    }
    removeObject(key, objectToRemove) {
        let objects = this.loadObjects(key);
        objects = objects.filter(obj => obj.id !== objectToRemove.id);
        this.saveObjects(key, objects);
    }
    getHistory() {
        return this.loadObjects(this.storageKey);
    }
    getFavorites() {
        return this.loadObjects(this.storageKey + 'Saved');
    }
    removeFavorite(objectToRemove) {
        this.removeObject(this.storageKey + 'Saved', objectToRemove);
    }
    manageHistory(user, hello) {
        this.addObject(this.storageKey, { id: user, user: hello.currentUser });
    }
    favoriteHistoryItem(newObject) {
        this.addObject(this.storageKey + 'Saved', newObject);
    }
    /**
     * Function to initiate the impersonate process triggered through the url, which can be called from other classes, e.g., RouterModule.
    */
    impersonate(user) {
        if (!this.userService) {
            return;
        }
        return forkJoin({
            hello: this.userService.helloUser(),
            whoAmI: this.userService.whoAmI()
        }).subscribe(({ hello, whoAmI }) => {
            if (whoAmI != (hello.impersonatingUser || hello.currentUser).username) {
                this.userService.stopImpersonating().subscribe(() => {
                    this.preImpersonatingChecks(user, hello);
                });
            }
            else {
                this.preImpersonatingChecks(user, hello);
            }
        });
    }
    /**
     * Function to hit the stop impersonating endpoint
    */
    stopImpersonating() {
        this.userService.stopImpersonating().subscribe(() => {
            this.landingPageRedirect();
        });
    }
    /**
     * This will check to see if the input is an LUID. If it is,
     * then it will hit the endpoint to get the corresponding
     * username. It will then call the function that actually impersonates.
     * @param userOrId
    */
    preImpersonatingChecks(userOrId, hello) {
        let err = false;
        /* if only digits, convert  */
        if (this.integersRegex.test(userOrId)) {
            try {
                userOrId = this.padLuid(userOrId);
            }
            catch (e) {
                err = true;
                this.displayErrorToast(userOrId);
            }
        }
        if (err)
            return;
        /**
         * If input matches a LUID, then hit the endpoint to get the username.
         * If it throws an error on that call OR if it doesn't match,
         * then call the impersonate endpoint as normal.
        */
        if (this.luidRegex.test(userOrId)) {
            this.userService.getUsernameFromLuid(userOrId).pipe(catchError(() => of({ value: userOrId }))).subscribe({
                next: (username) => this.startImpersonating(username.value),
                error: () => this.displayErrorToast(userOrId)
            });
        }
        else {
            this.startImpersonating(userOrId);
        }
    }
    /**
     * This function will hit the impersonate endpoint to begin
     * impersonating. It will display an error toast on an error
     * @param username
    */
    startImpersonating(username) {
        this.userService.impersonateUser(username).subscribe({
            next: (userInfo) => {
                this.manageHistory(username, userInfo);
                this.landingPageRedirect();
            },
            error: () => this.displayErrorToast(username)
        });
    }
    landingPageRedirect() {
        this.router.navigateByUrl('')
            .then(() => this.reloadPage())
            .catch(() => this.reloadPage());
    }
    /**
     * This will display a standard error message if the username
     * or LUID is invalid.
    */
    displayErrorToast(invalidUsername) {
        let message = "Make sure you're using a valid username or LU ID and try again. ";
        if (invalidUsername) {
            invalidUsername = invalidUsername.trim();
            message += "Username/ID used: " + invalidUsername;
        }
        //this could be nicer but its a dev only feature so its ok
        alert(message);
    }
    /**
     * This will pad a string of digits with a "L" and preceeding 0's
     * until it matches the standard LUID pattern. It will throw an
     * error if the length of the passed in string is greater than 8.
     * @throws error
     * @param digits
     * @returns string
    */
    padLuid(digits) {
        let chars = digits.length;
        if (chars > 8)
            throw new Error('Too many digits. The input is definitely not an username or LUID.');
        let response = digits;
        for (chars; chars < 8; chars++) {
            response = '0' + response;
        }
        return 'L' + response;
    }
    /**
     * This method reloads the window.
     * This is used for testing purposes
     * @returns void
    */
    reloadPage() {
        window.location.reload();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ImpersonateService, deps: [{ token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ImpersonateService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ImpersonateService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.Router }] });

//based on https://medium.com/allenhwkim/angular-trap-focus-with-in-a-block-79b7572f23c2
class TrapFocusDirective {
    constructor(el) {
        this.el = el;
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.trapFocus(this.el.nativeElement);
        }, 1000);
    }
    trapFocus(element) {
        const focusableEls1 = element.querySelectorAll('a[href], button, textarea, input[type="text"],' +
            'input[type="radio"], input[type="checkbox"], select');
        const focusableEls = Array.from(focusableEls1)
            .filter((el) => !el.disabled);
        const firstFocusableEl = focusableEls[0];
        const lastFocusableEl = focusableEls[focusableEls.length - 1];
        element.addEventListener('keydown', function (e) {
            var isTabPressed = e.keyCode === 9; // isTabPressed
            if (!isTabPressed)
                return;
            if (e.shiftKey) /* shift + tab */ {
                if (document.activeElement === firstFocusableEl) {
                    lastFocusableEl.focus();
                    e.preventDefault();
                }
            }
            else /* tab */ {
                if (document.activeElement === lastFocusableEl) {
                    firstFocusableEl.focus();
                    e.preventDefault();
                }
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: TrapFocusDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.1.1", type: TrapFocusDirective, isStandalone: true, selector: "[trapFocus]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: TrapFocusDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[trapFocus]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class SlModalComponent {
    constructor(modalService, cd, sanitizer) {
        this.modalService = modalService;
        this.cd = cd;
        this.sanitizer = sanitizer;
        this._afterClosed = new Subject();
        this.afterClosed = this._afterClosed.asObservable();
    }
    get class() {
        return this.modalConfig?.class; // the name of the class. 
    }
    onKeydownHandler() {
        this.remove();
    }
    ngAfterViewInit() {
        if (document.activeElement) {
            this.lastFocused = document.activeElement;
        }
        //If a component was passed it lets create it in a template
        if (this.modalConfig?.bodyComponent && this.inner) {
            let componentRef = undefined;
            if (this.modalConfig.injector) {
                componentRef = this.inner.createComponent(this.modalConfig.bodyComponent, {
                    environmentInjector: this.modalConfig.injector.get(EnvironmentInjector)
                });
            }
            else {
                componentRef = this.inner.createComponent(this.modalConfig.bodyComponent);
            }
            //If an component options were passed in add them to the component
            if (this.modalConfig.bodyComponentOpts) {
                for (const key in this.modalConfig.bodyComponentOpts) {
                    componentRef.setInput(key, this.modalConfig.bodyComponentOpts[key]);
                }
            }
            this.cd.detectChanges();
        }
        //If a template is passed it lets embed it into our modal
        if (this.modalConfig?.bodyTemplate && this.template) {
            this.template.createEmbeddedView(this.modalConfig?.bodyTemplate);
            this.cd.detectChanges();
        }
        if (this.modalConfig?.message) {
            const isHTML = RegExp.prototype.test.bind(/(<([^>]+)>)/i);
            if (isHTML(this.modalConfig?.message)) {
                this.messageIsHtml = this.sanitizer.bypassSecurityTrustHtml(this.modalConfig?.message);
                this.cd.detectChanges();
            }
        }
        this.modalContent.nativeElement.focus();
    }
    confirm() {
        if (this.modalConfig?.onConfirm) {
            this.modalConfig?.onConfirm();
        }
        this.remove();
    }
    remove(output) {
        this._afterClosed.next(output);
        if (this.modalConfig?.onClose)
            this.modalConfig?.onClose();
        if (this.selfReference)
            this.modalService.close(this.selfReference);
        if (this.lastFocused)
            this.lastFocused.focus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlModalComponent, deps: [{ token: SlModalService }, { token: i0.ChangeDetectorRef }, { token: i2$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlModalComponent, isStandalone: true, selector: "sl-modal", inputs: { modalConfig: "modalConfig", selfReference: "selfReference" }, host: { listeners: { "document:keydown.escape": "onKeydownHandler($event)" }, properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "inner", first: true, predicate: ["inner"], descendants: true, read: ViewContainerRef }, { propertyName: "template", first: true, predicate: ["template"], descendants: true, read: ViewContainerRef }, { propertyName: "modalContent", first: true, predicate: ["modalContent"], descendants: true }], ngImport: i0, template: "<div class=\"sl-modal\" [class.toastLike]=\"modalConfig?.type === 'toast'\" [attr.data-modal-type]=\"modalConfig?.type\" >\n    <!-- allow modal close when you click the overlay -->\n    <div *ngIf=\"modalConfig?.disableOverlayClick !== true && modalConfig?.type !== 'toast'\" class=\"overlay\"\n        (click)=\"remove()\" aria-label=\"close\"></div>\n\n    <!--don't allow modal close when you click the overlay -->\n    <div *ngIf=\"modalConfig?.disableOverlayClick === true && modalConfig?.type !== 'toast'\" class=\"overlay\">\n    </div>\n\n    <div class=\"sl-modal-body\" tabindex=\"0\" #modalContent trapFocus role=\"dialog\" aria-labelledby=\"modal-title\" aria-describedby=\"dialogDesc\" aria-modal=\"true\">\n        <div *ngIf=\"modalConfig?.type !== 'custom'\" class=\"modal-header\">\n            <p class=\"modal-title\">{{modalConfig?.title}}</p>\n            <button class=\"modal-close\" (click)=\"remove();\" role=\"button\" aria-label=\"Close popup\" type=\"button\"><svg\n                    xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"\n                    stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n                    class=\"feather feather-x\">\n                    <line x1=\"24\" y1=\"0\" x2=\"0\" y2=\"24\"></line>\n                    <line x1=\"0\" y1=\"0\" x2=\"24\" y2=\"24\"></line>\n                </svg></button>\n        </div>\n\n        <!-- Template for Toast Notification -->\n        <!-- This is a super basic toast notification, so no stacking, etc -->\n        <ng-template [ngIf]=\"modalConfig?.type === 'toast'\">\n            <p *ngIf=\"messageIsHtml === undefined\">{{modalConfig?.message}}</p>\n            <div *ngIf=\"messageIsHtml !== undefined\" [innerHTML]=\"messageIsHtml\"></div>\n            <button class=\"action-button\" (click)=\"remove();\" aria-label=\"OK\" type=\"button\">OK</button>\n        </ng-template>\n\n        <!-- Default template, just shows the title and message -->\n        <ng-template id=\"dialogDesc\" [ngIf]=\"!modalConfig?.type && !modalConfig?.bodyComponent && !modalConfig?.bodyTemplate\">\n            <p *ngIf=\"messageIsHtml === undefined\">{{modalConfig?.message}}</p>\n            <div *ngIf=\"messageIsHtml !== undefined\" [innerHTML]=\"messageIsHtml\"></div>\n        </ng-template>\n\n        <!-- Template if the user passed in a component to display inside the modal -->\n        <ng-template [ngIf]=\"modalConfig?.bodyComponent\">\n            <ng-container #inner></ng-container>\n        </ng-template>\n\n        <!-- Template if user passed in a template to display inside the modal -->\n        <ng-template [ngIf]=\"modalConfig?.bodyTemplate\">\n            <ng-container #template></ng-container>\n        </ng-template>\n\n        <!-- Template for alerts -->\n        <ng-template [ngIf]=\"modalConfig?.type === 'alert'\">\n            <p *ngIf=\"messageIsHtml === undefined\">{{modalConfig?.message}}</p>\n            <div *ngIf=\"messageIsHtml !== undefined\" [innerHTML]=\"messageIsHtml\"></div>\n            <button class=\"action-button\" (click)=\"remove();\" [attr.aria-label]=\"modalConfig?.confirmButtonText\" type=\"button\">{{modalConfig?.confirmButtonText ? modalConfig?.confirmButtonText :\n                'OK'}}</button>\n        </ng-template>\n\n        <!-- Template for confirms -->\n        <ng-template [ngIf]=\"modalConfig?.type === 'confirm'\">\n            <p *ngIf=\"messageIsHtml === undefined\">{{modalConfig?.message}}</p>\n            <div *ngIf=\"messageIsHtml !== undefined\" [innerHTML]=\"messageIsHtml\"></div>\n            <div class=\"btn-holder\">\n                <button (click)=\"remove();\" class=\"cancel-btn\" aria-label=\"Cancel\" type=\"button\">Cancel</button>\n                <button (click)=\"confirm();\" class=\"confirm-btn\" [attr.aria-label]=\"modalConfig?.confirmButtonText\" type=\"button\">{{modalConfig?.confirmButtonText ? modalConfig?.confirmButtonText :\n                    'OK'}}</button>\n            </div>\n        </ng-template>\n\n    </div>\n</div>", styles: [".sl-modal{display:flex;width:100%;height:100%;align-items:center;justify-content:center;position:fixed;top:0;left:0;z-index:1001}.sl-modal.toastLike{display:inline;height:auto;width:auto;position:initial}.overlay{width:100%;height:100%;background-color:#0006;position:absolute;animation:fadeIn .2s forwards;z-index:1001}.sl-modal-body{width:auto;height:auto;min-width:200px;min-height:200px;display:block;background:#fff;padding:20px;border-radius:5px;position:absolute;animation:fadeUpModal .2s forwards;z-index:1002}.toastLike .sl-modal-body{bottom:-120%;right:10px;position:fixed;min-height:100px;animation:fadeUpToast .3s forwards;display:flex;justify-content:space-between}.modal-header{display:flex;justify-content:space-between;align-items:flex-start;flex-direction:row;line-height:0}.modal-header p{margin:0;padding:0;align-self:center}.modal-close{border:none;background:transparent;margin:0;cursor:pointer;color:#000;width:14px;height:14px;padding:10px;border-radius:100%;box-sizing:content-box!important}.modal-close:hover{background:#0000001a}.modal-close svg{width:100%;height:100%}@keyframes fadeUpModal{0%{opacity:0;margin-top:50px;box-shadow:0 10px #000}to{opacity:1;margin-top:0;box-shadow:0 10px 50px #00000080}}@keyframes fadeUpToast{0%{opacity:0;bottom:-120%;box-shadow:0 10px #000}30%{opacity:1;bottom:40px;box-shadow:0 10px 50px #00000080}to{opacity:1;bottom:10px;box-shadow:0 10px 50px #00000080}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: TrapFocusDirective, selector: "[trapFocus]" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-modal', imports: [CommonModule, TrapFocusDirective], template: "<div class=\"sl-modal\" [class.toastLike]=\"modalConfig?.type === 'toast'\" [attr.data-modal-type]=\"modalConfig?.type\" >\n    <!-- allow modal close when you click the overlay -->\n    <div *ngIf=\"modalConfig?.disableOverlayClick !== true && modalConfig?.type !== 'toast'\" class=\"overlay\"\n        (click)=\"remove()\" aria-label=\"close\"></div>\n\n    <!--don't allow modal close when you click the overlay -->\n    <div *ngIf=\"modalConfig?.disableOverlayClick === true && modalConfig?.type !== 'toast'\" class=\"overlay\">\n    </div>\n\n    <div class=\"sl-modal-body\" tabindex=\"0\" #modalContent trapFocus role=\"dialog\" aria-labelledby=\"modal-title\" aria-describedby=\"dialogDesc\" aria-modal=\"true\">\n        <div *ngIf=\"modalConfig?.type !== 'custom'\" class=\"modal-header\">\n            <p class=\"modal-title\">{{modalConfig?.title}}</p>\n            <button class=\"modal-close\" (click)=\"remove();\" role=\"button\" aria-label=\"Close popup\" type=\"button\"><svg\n                    xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"\n                    stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n                    class=\"feather feather-x\">\n                    <line x1=\"24\" y1=\"0\" x2=\"0\" y2=\"24\"></line>\n                    <line x1=\"0\" y1=\"0\" x2=\"24\" y2=\"24\"></line>\n                </svg></button>\n        </div>\n\n        <!-- Template for Toast Notification -->\n        <!-- This is a super basic toast notification, so no stacking, etc -->\n        <ng-template [ngIf]=\"modalConfig?.type === 'toast'\">\n            <p *ngIf=\"messageIsHtml === undefined\">{{modalConfig?.message}}</p>\n            <div *ngIf=\"messageIsHtml !== undefined\" [innerHTML]=\"messageIsHtml\"></div>\n            <button class=\"action-button\" (click)=\"remove();\" aria-label=\"OK\" type=\"button\">OK</button>\n        </ng-template>\n\n        <!-- Default template, just shows the title and message -->\n        <ng-template id=\"dialogDesc\" [ngIf]=\"!modalConfig?.type && !modalConfig?.bodyComponent && !modalConfig?.bodyTemplate\">\n            <p *ngIf=\"messageIsHtml === undefined\">{{modalConfig?.message}}</p>\n            <div *ngIf=\"messageIsHtml !== undefined\" [innerHTML]=\"messageIsHtml\"></div>\n        </ng-template>\n\n        <!-- Template if the user passed in a component to display inside the modal -->\n        <ng-template [ngIf]=\"modalConfig?.bodyComponent\">\n            <ng-container #inner></ng-container>\n        </ng-template>\n\n        <!-- Template if user passed in a template to display inside the modal -->\n        <ng-template [ngIf]=\"modalConfig?.bodyTemplate\">\n            <ng-container #template></ng-container>\n        </ng-template>\n\n        <!-- Template for alerts -->\n        <ng-template [ngIf]=\"modalConfig?.type === 'alert'\">\n            <p *ngIf=\"messageIsHtml === undefined\">{{modalConfig?.message}}</p>\n            <div *ngIf=\"messageIsHtml !== undefined\" [innerHTML]=\"messageIsHtml\"></div>\n            <button class=\"action-button\" (click)=\"remove();\" [attr.aria-label]=\"modalConfig?.confirmButtonText\" type=\"button\">{{modalConfig?.confirmButtonText ? modalConfig?.confirmButtonText :\n                'OK'}}</button>\n        </ng-template>\n\n        <!-- Template for confirms -->\n        <ng-template [ngIf]=\"modalConfig?.type === 'confirm'\">\n            <p *ngIf=\"messageIsHtml === undefined\">{{modalConfig?.message}}</p>\n            <div *ngIf=\"messageIsHtml !== undefined\" [innerHTML]=\"messageIsHtml\"></div>\n            <div class=\"btn-holder\">\n                <button (click)=\"remove();\" class=\"cancel-btn\" aria-label=\"Cancel\" type=\"button\">Cancel</button>\n                <button (click)=\"confirm();\" class=\"confirm-btn\" [attr.aria-label]=\"modalConfig?.confirmButtonText\" type=\"button\">{{modalConfig?.confirmButtonText ? modalConfig?.confirmButtonText :\n                    'OK'}}</button>\n            </div>\n        </ng-template>\n\n    </div>\n</div>", styles: [".sl-modal{display:flex;width:100%;height:100%;align-items:center;justify-content:center;position:fixed;top:0;left:0;z-index:1001}.sl-modal.toastLike{display:inline;height:auto;width:auto;position:initial}.overlay{width:100%;height:100%;background-color:#0006;position:absolute;animation:fadeIn .2s forwards;z-index:1001}.sl-modal-body{width:auto;height:auto;min-width:200px;min-height:200px;display:block;background:#fff;padding:20px;border-radius:5px;position:absolute;animation:fadeUpModal .2s forwards;z-index:1002}.toastLike .sl-modal-body{bottom:-120%;right:10px;position:fixed;min-height:100px;animation:fadeUpToast .3s forwards;display:flex;justify-content:space-between}.modal-header{display:flex;justify-content:space-between;align-items:flex-start;flex-direction:row;line-height:0}.modal-header p{margin:0;padding:0;align-self:center}.modal-close{border:none;background:transparent;margin:0;cursor:pointer;color:#000;width:14px;height:14px;padding:10px;border-radius:100%;box-sizing:content-box!important}.modal-close:hover{background:#0000001a}.modal-close svg{width:100%;height:100%}@keyframes fadeUpModal{0%{opacity:0;margin-top:50px;box-shadow:0 10px #000}to{opacity:1;margin-top:0;box-shadow:0 10px 50px #00000080}}@keyframes fadeUpToast{0%{opacity:0;bottom:-120%;box-shadow:0 10px #000}30%{opacity:1;bottom:40px;box-shadow:0 10px 50px #00000080}to{opacity:1;bottom:10px;box-shadow:0 10px 50px #00000080}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}\n"] }]
        }], ctorParameters: () => [{ type: SlModalService }, { type: i0.ChangeDetectorRef }, { type: i2$1.DomSanitizer }], propDecorators: { inner: [{
                type: ViewChild,
                args: ['inner', { read: ViewContainerRef }]
            }], template: [{
                type: ViewChild,
                args: ['template', { read: ViewContainerRef }]
            }], modalContent: [{
                type: ViewChild,
                args: ['modalContent', { static: false }]
            }], modalConfig: [{
                type: Input
            }], selfReference: [{
                type: Input
            }], class: [{
                type: HostBinding,
                args: ['class']
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }] } });

class SlModalService {
    constructor(appRef) {
        this.appRef = appRef;
    }
    /**
     *
     * @param {IModal} modalConfig
     * @returns {SlModalComponent}
     * @see {@link https://wiki.os.liberty.edu/display/SL/SL+Modal}
     * @example
     * //Basic Modal
     * this.slModalService.createModal({
     *  title:'My Basic Modal',
     *  message: 'Wow this modal is pretty cool'
     * })
     *
     */
    createModal(modalConfig) {
        const modalComponentRef = createComponent(SlModalComponent, {
            environmentInjector: this.appRef.injector
        });
        // attach view so angular change detection works
        this.appRef.attachView(modalComponentRef.hostView);
        // set inputs aka pass in the config
        modalComponentRef.setInput('modalConfig', modalConfig);
        modalComponentRef.setInput('selfReference', modalComponentRef);
        // append it to the body, so we can, ya know, see it and stuff
        document.body.append(modalComponentRef.hostView.rootNodes[0]);
        return modalComponentRef.instance;
    }
    /**
     *
     * @param ref
     * @description Takes the ComponentRef from the modal and detaches and destroys it
     */
    close(ref) {
        this.appRef.detachView(ref.hostView);
        // destroy the component
        ref.destroy();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlModalService, deps: [{ token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlModalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i0.ApplicationRef }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Remove all white spaces and convert to Upper Case
 * @param str
 */
function prepareString(str) {
    return str.replace(/\s/g, '').toUpperCase();
}
/**
 * Validation function to check if input is not null
 * @returns ValidatorFn
 */
function customRequired() {
    return (control) => {
        /* check if sanitized value is not null */
        const len = prepareString(control.value).length;
        return len <= 0 ? { 'customRequired': { value: control.value } } : null;
    };
}
/**
 * Validation function to check if input is all digits. If yes, it checks
 * if the length is not greater than 8
 * @returns ValidatorFn
 */
function digitCheck() {
    const integersRegex = new RegExp(/^\d+$/);
    return (control) => {
        /* check if input is all digits and is less than 8 */
        const val = prepareString(control.value);
        return (integersRegex.test(val) && val.length > 8) ? { 'digitCheck': { value: val } } : null;
    };
}
/**
 * Validation function to check if input matches a 'bad' LUID.
 * L+(1 to 7) OR L+(9 or more) digits
 * @returns ValidatorFn
 */
function luidCheck() {
    const badLuidRegex = new RegExp(/^L\d{1,7}$|^L\d{9,}$/i);
    return (control) => {
        /* check if input is a bad luid */
        const val = prepareString(control.value);
        return badLuidRegex.test(val) ? { 'luidCheck': { value: control.value } } : null;
    };
}
class SlImpersonateComponent {
    constructor(impersonateService, modalService, config) {
        this.impersonateService = impersonateService;
        this.modalService = modalService;
        this.config = config;
        this.showOrHide = false;
        this.impersonating = false;
        this.canImpersonate = false;
        this.impersonateForm = new FormGroup({
            impersonateInput: new FormControl('', [
                Validators.required,
                customRequired(),
                digitCheck(),
                luidCheck()
            ])
        });
        this.userService = inject(this.config.userService);
        this.history = [];
        this.favorites = [];
    }
    ngOnInit() {
        this.impersonateService.setUserService(this.userService);
        this.impersonateService.setHistoryKey(this.config.historyKey);
        this.getHistory();
        this.getFavorites();
        this.userService.helloUser().subscribe((x) => {
            this.user = x;
            this.canImpersonate = x.roles.some((role) => role.includes(this.config.switchUserRole));
            if (this.user.impersonatingUser) {
                this.impersonating = true;
            }
            else {
                this.impersonating = false;
            }
        });
    }
    ngAfterViewInit() {
        this.usernameIdInput.nativeElement.focus();
    }
    impersonate(user) {
        if (user) {
            this.impersonateService.impersonate(prepareString(user));
            this.slideIn();
        }
        else {
            this.impersonateService.impersonate(prepareString(this.impersonateForm.controls.impersonateInput.value));
        }
    }
    stopImpersonating() {
        this.impersonateService.stopImpersonating();
    }
    switchUser() {
        this.slideIn();
    }
    favorite(item) {
        this.impersonateService.favoriteHistoryItem(item);
        this.getFavorites();
    }
    removeFavorite(item) {
        this.impersonateService.removeFavorite(item);
        this.getFavorites();
    }
    showHide() {
        this.showOrHide = !this.showOrHide;
    }
    getHistory() {
        this.history = this.impersonateService.getHistory();
    }
    getFavorites() {
        this.favorites = this.impersonateService.getFavorites();
    }
    deleteHistory() {
        this.impersonateService.deleteObjects(this.config.historyKey);
    }
    showUserInfo() {
        this.userInfoModal = this.modalService.createModal({
            bodyTemplate: this.userInfo
        });
    }
    slideIn() {
        const overlay = this.filterOverlay.nativeElement;
        const contents = this.contents.nativeElement;
        const contentHolder = this.holder.nativeElement;
        const body = document.querySelector('body');
        let bodyOverflow = body.style.overflow;
        contentHolder.classList.toggle('active');
        overlay.classList.toggle('active');
        contents.classList.toggle('active');
        bodyOverflow === 'hidden' ? bodyOverflow = 'auto' : bodyOverflow = 'auto';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlImpersonateComponent, deps: [{ token: ImpersonateService }, { token: SlModalService }, { token: 'IMPERSONATE_CONFIG' }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.1.1", type: SlImpersonateComponent, isStandalone: true, selector: "sl-impersonate", viewQueries: [{ propertyName: "contents", first: true, predicate: ["contents"], descendants: true }, { propertyName: "filterOverlay", first: true, predicate: ["filterOverlay"], descendants: true }, { propertyName: "holder", first: true, predicate: ["holder"], descendants: true }, { propertyName: "usernameIdInput", first: true, predicate: ["usernameIdInput"], descendants: true }, { propertyName: "userInfo", first: true, predicate: ["userInfo"], descendants: true, read: TemplateRef }], ngImport: i0, template: "<div *ngIf=\"canImpersonate || impersonating === true\" class=\"info v2\" ngClass=\"{{showOrHide ? 'hide' : 'show'}}\">\n\n    <button (click)=\"switchUser();\" aria-label=\"switch user\" tabindex=\"-1\">\n\n        <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"\n            stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n            class=\"feather feather-users\">\n            <path d=\"M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2\"></path>\n            <circle cx=\"9\" cy=\"7\" r=\"4\"></circle>\n            <path d=\"M23 21v-2a4 4 0 0 0-3-3.87\"></path>\n            <path d=\"M16 3.13a4 4 0 0 1 0 7.75\"></path>\n        </svg>\n    </button>\n    <button *ngIf=\"impersonating\" (click)=\"stopImpersonating();\" aria-label=\"stop impersonating\" tabindex=\"-1\"><svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\"\n            height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\"\n            stroke-linejoin=\"round\" class=\"feather feather-user-x\">\n            <path d=\"M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2\"></path>\n            <circle cx=\"8.5\" cy=\"7\" r=\"4\"></circle>\n            <line x1=\"18\" y1=\"8\" x2=\"23\" y2=\"13\"></line>\n            <line x1=\"23\" y1=\"8\" x2=\"18\" y2=\"13\"></line>\n        </svg></button>\n    <div class=\"user\">\n        <button (click)=\"showUserInfo()\" aria-label=\"show user info\" tabindex=\"-1\">\n            <img src=\"{{user?.currentUser.photoUrl}}\" alt=\"\">\n        </button>\n    </div>\n</div>\n\n\n<ng-template #userInfo>\n    <div class=\"user-info\">\n\n        <img src=\"{{user?.currentUser.photoUrl}}\" alt=\"\">\n\n        <div class=\"title\">\n            {{user.currentUser.firstName}}\n            <span>{{user.currentUser.lastName}}</span>\n        </div>\n        <div class=\"sub-info\">\n            <div>\n                <span>LUID</span>\n                {{user.currentUser.luId}}\n            </div>\n            <div>\n                <span>PIDM</span>\n                {{user.currentUser.pidm}}\n            </div>\n\n        </div>\n\n        <div class=\"user-roles\">\n            <div class=\"sub-title\">\n                Roles:\n            </div>\n            @for (role of user.roles; track $index) {\n            <div>{{role}}</div>\n            }\n        </div>\n    </div>\n</ng-template>\n\n\n<!-- this needs to be moved over into a ng-template and made to use the sl-modal but I didnt have time this round -->\n<div class=\"content-holder\" #holder>\n    <div class=\"filter-overlay\" #filterOverlay (click)=\"slideIn()\"></div>\n\n    <div class=\"contents\" #contents>\n        <form [formGroup]=\"impersonateForm\" class=\"form-body\" (ngSubmit)=\"impersonate()\">\n            <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"\n                stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n                class=\"feather feather-users impersonate-icon\">\n                <path d=\"M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2\"></path>\n                <circle cx=\"9\" cy=\"7\" r=\"4\"></circle>\n                <path d=\"M23 21v-2a4 4 0 0 0-3-3.87\"></path>\n                <path d=\"M16 3.13a4 4 0 0 1 0 7.75\"></path>\n            </svg>\n\n            <p class=\"title\">Impersonate a User</p>\n            <p class=\"blurb\">Use the application as if you are someone else.</p>\n\n\n            <div class=\"input-section\">\n                <label for=\"impersonate-username\">Enter a username or LU ID</label>\n                <input #usernameIdInput type=\"text\" id=\"impersonate-username\" class=\"form-control lu-form-control\"\n                    placeholder=\"user to impersonate\" formControlName=\"impersonateInput\" autofocus>\n            </div>\n\n            <div class=\"history\">\n                <p class=\"impersonate-history-title\">Favorites:</p>\n                <ul>\n                    <li class=\"impersonate-history-item\" *ngFor=\"let record of favorites\">\n                        <a (click)=\"removeFavorite(record)\" class=\"remove-btn\"><svg xmlns=\"http://www.w3.org/2000/svg\"\n                                width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"\n                                stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n                                class=\"feather feather-x-circle\">\n                                <circle cx=\"12\" cy=\"12\" r=\"10\"></circle>\n                                <line x1=\"15\" y1=\"9\" x2=\"9\" y2=\"15\"></line>\n                                <line x1=\"9\" y1=\"9\" x2=\"15\" y2=\"15\"></line>\n                            </svg></a>\n\n                        <a (click)=\"impersonate(record.id)\" class=\"impersonate-btn\">\n                            <div class=\"impersonate-history-info\">{{record.id}} <span>{{record.user?.luId}}</span></div>\n                        </a>\n                    </li>\n                </ul>\n                <p class=\"impersonate-history-title\">Last ten impersonated:</p>\n                <ul class=\"impersonate-history\">\n                    <li class=\"impersonate-history-item\" *ngFor=\"let record of history\">\n                        <a (click)=\"favorite(record)\" class=\"favorite-btn\"><svg xmlns=\"http://www.w3.org/2000/svg\"\n                                width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"\n                                stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n                                class=\"feather feather-star\">\n                                <polygon\n                                    points=\"12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2\">\n                                </polygon>\n                            </svg></a>\n                        <a (click)=\"impersonate(record.id)\" class=\"impersonate-btn\">\n                            <div class=\"impersonate-history-info\">{{record.id}} <span>{{record.user?.luId}}</span></div>\n                        </a>\n\n                    </li>\n                </ul>\n                <!-- <button (click)=\"deleteHistory()\">Delete History</button> -->\n            </div>\n\n            <div class=\"action-btns\">\n                <button type=\"button\" (click)=\"slideIn()\" class=\"cancel-btn\">Cancel</button>\n                <button type=\"submit\" class=\"submit-btn\" [disabled]=\"this.impersonateForm.invalid\">Impersonate</button>\n            </div>\n\n        </form>\n    </div>\n</div>", styles: ["@import\"https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700&display=swap\";:host{font-family:Atkinson Hyperlegible,sans-serif;font-size:16px}.openTools{border-radius:100%;width:40px;height:40px;position:fixed;bottom:-60px;right:10px;z-index:9999;filter:drop-shadow(3px 6px black);background:#fff!important;color:#000;transition:all .1s linear}.openTools svg{width:20px}.openTools.active{bottom:10px}.openTools.active:hover{bottom:14px}.info{height:60px;position:fixed;z-index:9998;filter:drop-shadow(3px 5px #000000);background:#fff!important;color:#000;border-radius:10px;transition:all .2s linear;border:2px solid black;bottom:10px;display:flex;align-items:center;gap:20px}.info.hide{right:-100%}.info button{height:30px;background-color:transparent;color:#000;width:30px;padding:0;display:flex;align-items:center;justify-content:center}.info button:hover{color:#00f}.info .hide-btn{margin-right:-24px}.info img{height:40px;border-radius:100%}.v2{height:auto;width:auto;flex-direction:column;bottom:50vh;right:0;padding:15px 5px;border-radius:10px 0 0 10px}.user-info{font-family:Atkinson Hyperlegible,sans-serif;max-width:75ch;display:flex;flex-direction:column;align-items:center;gap:20px}.user-info img{width:100px;border-radius:100%}.user-info .title{font-size:1.5rem;font-weight:700}.user-info .sub-title{font-size:1.25rem;font-weight:700}.user-info .sub-info{font-weight:500;display:flex;flex-direction:column;align-items:center;gap:10px}.user-info .sub-info span{font-weight:700}.user-info .user-roles{display:flex;flex-direction:column;gap:10px}:host ::ng-deep sl-modal .sl-modal-body{border:2px solid black!important;border-radius:5px!important;filter:drop-shadow(7px 14px #000000)!important;box-shadow:0 0!important}.user{display:flex;align-items:center;gap:8px;font-size:14px}.user div{display:flex;flex-direction:column}.user .name{font-size:16px;font-weight:800}.content-holder{display:none;width:100%;height:100%;align-items:center;justify-content:center;position:fixed;top:0;left:0;z-index:9999}.content-holder.active{display:flex}.contents{width:auto;height:auto;min-width:200px;min-height:200px;display:block;background:#fff;color:#000;padding:40px;border-radius:5px;position:absolute;display:none;border:2px solid black}.contents p,.contents label{color:#000;font-size:1em}.contents label{font-weight:800}.contents .title{color:#000;font-size:1.2em;font-weight:800;text-align:center}.contents .blurb{color:#000;margin-bottom:20px;width:350px;text-align:center;padding:0 30px}.contents form{display:flex;align-items:center;flex-direction:column;gap:20px}.contents.active{display:flex;gap:20px;animation:fadeUpModal .2s forwards}.action-btns{display:flex;justify-content:space-between;width:100%;gap:30px}.action-btns button{color:#000;font-size:1em;padding:10px;border-radius:5px;border:2px solid black;height:40px;font-weight:800;background:transparent}.cancel-btn{width:40%}.submit-btn{width:60%;background:#00f!important;border-color:#000!important;color:#fff!important}.submit-btn:disabled{opacity:.5;background-color:gray!important}.history{display:flex;flex-direction:column;gap:20px;padding:30px;border-radius:5px;width:100%;border:2px solid}.history ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:10px;max-height:56px;overflow-y:auto}.history ul li:hover{font-weight:800}.history ul a{color:#000;cursor:pointer}.impersonate-history-title{font-weight:800}.impersonate-history{max-height:140px!important}.impersonate-history-item{display:flex;gap:10px;align-items:center;font-size:.875em}.impersonate-history-item span{font-size:.875em}.impersonate-history-item svg{height:auto;width:16px}.impersonate-history-info{width:100%;display:flex;gap:10px}.input-section{display:flex;flex-direction:column;gap:20px;padding:30px;border-radius:5px;width:100%;border:2px solid}.input-section input{padding:15px;border-radius:5px;border:0;background:#fff;color:#000;border:2px solid;font-weight:800}.favorite-btn svg{fill:#6b6b6b;stroke:#383838}.favorite-btn:hover svg{fill:#fede65;stroke:#fbc702}.impersonate-btn{color:#6b6b6b!important}.impersonate-btn:hover{color:#00f!important}.impersonate-icon{color:#00f;width:60px;height:auto}.history-section{display:flex}.fadeIn{opacity:0;animation:fadeIn .2s reverse}@keyframes fadeUpModal{0%{opacity:0;margin-top:50px}to{opacity:1;margin-top:0;filter:drop-shadow(7px 14px #000000)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}.filter-overlay{display:none;width:100%;height:100%;background-color:#0006;position:absolute}.filter-overlay.active{display:block;animation:fadeIn .2s forwards}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "ngmodule", type: FormsModule }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlImpersonateComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-impersonate', imports: [CommonModule, ReactiveFormsModule, FormsModule], template: "<div *ngIf=\"canImpersonate || impersonating === true\" class=\"info v2\" ngClass=\"{{showOrHide ? 'hide' : 'show'}}\">\n\n    <button (click)=\"switchUser();\" aria-label=\"switch user\" tabindex=\"-1\">\n\n        <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"\n            stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n            class=\"feather feather-users\">\n            <path d=\"M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2\"></path>\n            <circle cx=\"9\" cy=\"7\" r=\"4\"></circle>\n            <path d=\"M23 21v-2a4 4 0 0 0-3-3.87\"></path>\n            <path d=\"M16 3.13a4 4 0 0 1 0 7.75\"></path>\n        </svg>\n    </button>\n    <button *ngIf=\"impersonating\" (click)=\"stopImpersonating();\" aria-label=\"stop impersonating\" tabindex=\"-1\"><svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\"\n            height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\"\n            stroke-linejoin=\"round\" class=\"feather feather-user-x\">\n            <path d=\"M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2\"></path>\n            <circle cx=\"8.5\" cy=\"7\" r=\"4\"></circle>\n            <line x1=\"18\" y1=\"8\" x2=\"23\" y2=\"13\"></line>\n            <line x1=\"23\" y1=\"8\" x2=\"18\" y2=\"13\"></line>\n        </svg></button>\n    <div class=\"user\">\n        <button (click)=\"showUserInfo()\" aria-label=\"show user info\" tabindex=\"-1\">\n            <img src=\"{{user?.currentUser.photoUrl}}\" alt=\"\">\n        </button>\n    </div>\n</div>\n\n\n<ng-template #userInfo>\n    <div class=\"user-info\">\n\n        <img src=\"{{user?.currentUser.photoUrl}}\" alt=\"\">\n\n        <div class=\"title\">\n            {{user.currentUser.firstName}}\n            <span>{{user.currentUser.lastName}}</span>\n        </div>\n        <div class=\"sub-info\">\n            <div>\n                <span>LUID</span>\n                {{user.currentUser.luId}}\n            </div>\n            <div>\n                <span>PIDM</span>\n                {{user.currentUser.pidm}}\n            </div>\n\n        </div>\n\n        <div class=\"user-roles\">\n            <div class=\"sub-title\">\n                Roles:\n            </div>\n            @for (role of user.roles; track $index) {\n            <div>{{role}}</div>\n            }\n        </div>\n    </div>\n</ng-template>\n\n\n<!-- this needs to be moved over into a ng-template and made to use the sl-modal but I didnt have time this round -->\n<div class=\"content-holder\" #holder>\n    <div class=\"filter-overlay\" #filterOverlay (click)=\"slideIn()\"></div>\n\n    <div class=\"contents\" #contents>\n        <form [formGroup]=\"impersonateForm\" class=\"form-body\" (ngSubmit)=\"impersonate()\">\n            <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"\n                stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n                class=\"feather feather-users impersonate-icon\">\n                <path d=\"M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2\"></path>\n                <circle cx=\"9\" cy=\"7\" r=\"4\"></circle>\n                <path d=\"M23 21v-2a4 4 0 0 0-3-3.87\"></path>\n                <path d=\"M16 3.13a4 4 0 0 1 0 7.75\"></path>\n            </svg>\n\n            <p class=\"title\">Impersonate a User</p>\n            <p class=\"blurb\">Use the application as if you are someone else.</p>\n\n\n            <div class=\"input-section\">\n                <label for=\"impersonate-username\">Enter a username or LU ID</label>\n                <input #usernameIdInput type=\"text\" id=\"impersonate-username\" class=\"form-control lu-form-control\"\n                    placeholder=\"user to impersonate\" formControlName=\"impersonateInput\" autofocus>\n            </div>\n\n            <div class=\"history\">\n                <p class=\"impersonate-history-title\">Favorites:</p>\n                <ul>\n                    <li class=\"impersonate-history-item\" *ngFor=\"let record of favorites\">\n                        <a (click)=\"removeFavorite(record)\" class=\"remove-btn\"><svg xmlns=\"http://www.w3.org/2000/svg\"\n                                width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"\n                                stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n                                class=\"feather feather-x-circle\">\n                                <circle cx=\"12\" cy=\"12\" r=\"10\"></circle>\n                                <line x1=\"15\" y1=\"9\" x2=\"9\" y2=\"15\"></line>\n                                <line x1=\"9\" y1=\"9\" x2=\"15\" y2=\"15\"></line>\n                            </svg></a>\n\n                        <a (click)=\"impersonate(record.id)\" class=\"impersonate-btn\">\n                            <div class=\"impersonate-history-info\">{{record.id}} <span>{{record.user?.luId}}</span></div>\n                        </a>\n                    </li>\n                </ul>\n                <p class=\"impersonate-history-title\">Last ten impersonated:</p>\n                <ul class=\"impersonate-history\">\n                    <li class=\"impersonate-history-item\" *ngFor=\"let record of history\">\n                        <a (click)=\"favorite(record)\" class=\"favorite-btn\"><svg xmlns=\"http://www.w3.org/2000/svg\"\n                                width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"\n                                stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n                                class=\"feather feather-star\">\n                                <polygon\n                                    points=\"12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2\">\n                                </polygon>\n                            </svg></a>\n                        <a (click)=\"impersonate(record.id)\" class=\"impersonate-btn\">\n                            <div class=\"impersonate-history-info\">{{record.id}} <span>{{record.user?.luId}}</span></div>\n                        </a>\n\n                    </li>\n                </ul>\n                <!-- <button (click)=\"deleteHistory()\">Delete History</button> -->\n            </div>\n\n            <div class=\"action-btns\">\n                <button type=\"button\" (click)=\"slideIn()\" class=\"cancel-btn\">Cancel</button>\n                <button type=\"submit\" class=\"submit-btn\" [disabled]=\"this.impersonateForm.invalid\">Impersonate</button>\n            </div>\n\n        </form>\n    </div>\n</div>", styles: ["@import\"https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700&display=swap\";:host{font-family:Atkinson Hyperlegible,sans-serif;font-size:16px}.openTools{border-radius:100%;width:40px;height:40px;position:fixed;bottom:-60px;right:10px;z-index:9999;filter:drop-shadow(3px 6px black);background:#fff!important;color:#000;transition:all .1s linear}.openTools svg{width:20px}.openTools.active{bottom:10px}.openTools.active:hover{bottom:14px}.info{height:60px;position:fixed;z-index:9998;filter:drop-shadow(3px 5px #000000);background:#fff!important;color:#000;border-radius:10px;transition:all .2s linear;border:2px solid black;bottom:10px;display:flex;align-items:center;gap:20px}.info.hide{right:-100%}.info button{height:30px;background-color:transparent;color:#000;width:30px;padding:0;display:flex;align-items:center;justify-content:center}.info button:hover{color:#00f}.info .hide-btn{margin-right:-24px}.info img{height:40px;border-radius:100%}.v2{height:auto;width:auto;flex-direction:column;bottom:50vh;right:0;padding:15px 5px;border-radius:10px 0 0 10px}.user-info{font-family:Atkinson Hyperlegible,sans-serif;max-width:75ch;display:flex;flex-direction:column;align-items:center;gap:20px}.user-info img{width:100px;border-radius:100%}.user-info .title{font-size:1.5rem;font-weight:700}.user-info .sub-title{font-size:1.25rem;font-weight:700}.user-info .sub-info{font-weight:500;display:flex;flex-direction:column;align-items:center;gap:10px}.user-info .sub-info span{font-weight:700}.user-info .user-roles{display:flex;flex-direction:column;gap:10px}:host ::ng-deep sl-modal .sl-modal-body{border:2px solid black!important;border-radius:5px!important;filter:drop-shadow(7px 14px #000000)!important;box-shadow:0 0!important}.user{display:flex;align-items:center;gap:8px;font-size:14px}.user div{display:flex;flex-direction:column}.user .name{font-size:16px;font-weight:800}.content-holder{display:none;width:100%;height:100%;align-items:center;justify-content:center;position:fixed;top:0;left:0;z-index:9999}.content-holder.active{display:flex}.contents{width:auto;height:auto;min-width:200px;min-height:200px;display:block;background:#fff;color:#000;padding:40px;border-radius:5px;position:absolute;display:none;border:2px solid black}.contents p,.contents label{color:#000;font-size:1em}.contents label{font-weight:800}.contents .title{color:#000;font-size:1.2em;font-weight:800;text-align:center}.contents .blurb{color:#000;margin-bottom:20px;width:350px;text-align:center;padding:0 30px}.contents form{display:flex;align-items:center;flex-direction:column;gap:20px}.contents.active{display:flex;gap:20px;animation:fadeUpModal .2s forwards}.action-btns{display:flex;justify-content:space-between;width:100%;gap:30px}.action-btns button{color:#000;font-size:1em;padding:10px;border-radius:5px;border:2px solid black;height:40px;font-weight:800;background:transparent}.cancel-btn{width:40%}.submit-btn{width:60%;background:#00f!important;border-color:#000!important;color:#fff!important}.submit-btn:disabled{opacity:.5;background-color:gray!important}.history{display:flex;flex-direction:column;gap:20px;padding:30px;border-radius:5px;width:100%;border:2px solid}.history ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:10px;max-height:56px;overflow-y:auto}.history ul li:hover{font-weight:800}.history ul a{color:#000;cursor:pointer}.impersonate-history-title{font-weight:800}.impersonate-history{max-height:140px!important}.impersonate-history-item{display:flex;gap:10px;align-items:center;font-size:.875em}.impersonate-history-item span{font-size:.875em}.impersonate-history-item svg{height:auto;width:16px}.impersonate-history-info{width:100%;display:flex;gap:10px}.input-section{display:flex;flex-direction:column;gap:20px;padding:30px;border-radius:5px;width:100%;border:2px solid}.input-section input{padding:15px;border-radius:5px;border:0;background:#fff;color:#000;border:2px solid;font-weight:800}.favorite-btn svg{fill:#6b6b6b;stroke:#383838}.favorite-btn:hover svg{fill:#fede65;stroke:#fbc702}.impersonate-btn{color:#6b6b6b!important}.impersonate-btn:hover{color:#00f!important}.impersonate-icon{color:#00f;width:60px;height:auto}.history-section{display:flex}.fadeIn{opacity:0;animation:fadeIn .2s reverse}@keyframes fadeUpModal{0%{opacity:0;margin-top:50px}to{opacity:1;margin-top:0;filter:drop-shadow(7px 14px #000000)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}.filter-overlay{display:none;width:100%;height:100%;background-color:#0006;position:absolute}.filter-overlay.active{display:block;animation:fadeIn .2s forwards}\n"] }]
        }], ctorParameters: () => [{ type: ImpersonateService }, { type: SlModalService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['IMPERSONATE_CONFIG']
                }] }], propDecorators: { contents: [{
                type: ViewChild,
                args: ['contents']
            }], filterOverlay: [{
                type: ViewChild,
                args: ['filterOverlay']
            }], holder: [{
                type: ViewChild,
                args: ['holder']
            }], usernameIdInput: [{
                type: ViewChild,
                args: ['usernameIdInput']
            }], userInfo: [{
                type: ViewChild,
                args: ['userInfo', { read: TemplateRef }]
            }] } });

class SlInputComponent extends CVABase {
    constructor() {
        super(...arguments);
        this.placeholder = '';
        /**
         * optionally take in a text-like type attribute to alter the element
         * with input restrictions and provide specialized keyboards on mobile
         */
        this.type = 'text';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlInputComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlInputComponent, isStandalone: true, selector: "sl-input", inputs: { name: "name", label: "label", placeholder: "placeholder", minLength: "minLength", maxLength: "maxLength", min: "min", max: "max", step: "step", icon: "icon", identifier: "identifier", type: "type" }, host: { properties: { "class.hasError": "this.errorMessage" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SlInputComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<label *ngIf=\"label\" [for]=\"identifier\">{{ label }}</label>\n<div class=\"sl-input\">\n    <input\n    [type]=\"type\"\n    [id]=\"identifier\"\n    [name]=\"name\"\n    [placeholder]=\"placeholder\"\n    [disabled]=\"disabled\"\n    [attr.min]=\"min\"\n    [attr.max]=\"max\"\n    [attr.step]=\"step\"\n    [attr.minlength]=\"minLength\"\n    [attr.maxlength]=\"maxLength\"\n    [attr.aria-describedby]=\"identifier+'-error'\"\n    [(ngModel)]=\"value\"\n    [ngClass]=\"{ 'with-icon': icon }\"\n    />\n    <sl-icon *ngIf=\"icon\" [icon]=\"icon\" aria-hidden=\"true\" class=\"icon\"></sl-icon>\n\n    <span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n        <ng-content select=\"sl-validation-error-message\"></ng-content>\n    </span>\n\n</div>\n", styles: [""], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: SlIconComponent, selector: "sl-icon", inputs: ["icon", "path", "title", "flip", "colorFill", "colorStroke", "matchFontSize"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-input', imports: [CommonModule, FormsModule, SlIconComponent], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SlInputComponent),
                            multi: true,
                        },
                    ], template: "<label *ngIf=\"label\" [for]=\"identifier\">{{ label }}</label>\n<div class=\"sl-input\">\n    <input\n    [type]=\"type\"\n    [id]=\"identifier\"\n    [name]=\"name\"\n    [placeholder]=\"placeholder\"\n    [disabled]=\"disabled\"\n    [attr.min]=\"min\"\n    [attr.max]=\"max\"\n    [attr.step]=\"step\"\n    [attr.minlength]=\"minLength\"\n    [attr.maxlength]=\"maxLength\"\n    [attr.aria-describedby]=\"identifier+'-error'\"\n    [(ngModel)]=\"value\"\n    [ngClass]=\"{ 'with-icon': icon }\"\n    />\n    <sl-icon *ngIf=\"icon\" [icon]=\"icon\" aria-hidden=\"true\" class=\"icon\"></sl-icon>\n\n    <span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n        <ng-content select=\"sl-validation-error-message\"></ng-content>\n    </span>\n\n</div>\n" }]
        }], propDecorators: { errorMessage: [{
                type: HostBinding,
                args: ['class.hasError']
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], minLength: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], step: [{
                type: Input
            }], icon: [{
                type: Input
            }], identifier: [{
                type: Input
            }], type: [{
                type: Input
            }] } });

var EKeys;
(function (EKeys) {
    EKeys["ARROW_UP"] = "ArrowUp";
    EKeys["ARROW_DOWN"] = "ArrowDown";
    EKeys["ARROW_LEFT"] = "ArrowLeft";
    EKeys["ARROW_RIGHT"] = "ArrowRight";
    EKeys["SPACE"] = " ";
    EKeys["TAB"] = "Tab";
    EKeys["ENTER"] = "Enter";
    EKeys["ESCAPE"] = "Escape";
    EKeys["BACKSPACE"] = "Backspace";
})(EKeys || (EKeys = {}));
;
function isAlphanumeric(key) {
    return /^\w$/.test(key);
}

/**
 * Checks if a given HTML element can receive focus
 *
 * @param element - The HTML element to check.
 * @returns A boolean indicating whether the element can receive focus.
 *
 * @remarks
 * This function considers the following criteria for focusability:
 * - The element has an explicit `tabindex` attribute set to a value other than `-1`.
 * - The element is a naturally focusable element (e.g., button, iframe, input, object, select, textarea, summary)
 *   and is visible and not disabled.
 */
function canReceiveFocus(element) {
    //based on MDN's page on tabindex: https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex
    const focusableElements = ['BUTTON', 'IFRAME', 'INPUT', 'OBJECT', 'SELECT', 'TEXTAREA', 'SUMMARY'];
    const focusableElementsWithHref = ['A', 'AREA'];
    if (!element)
        return false;
    const hasTabIndex = element.getAttribute('tabindex') !== null && element.getAttribute('tabindex') !== '-1';
    const isNaturallyFocusable = focusableElements.includes(element.tagName)
        || (focusableElementsWithHref.includes(element.tagName) && !!element.getAttribute('href'));
    const isVisible = window.getComputedStyle(element).display != 'none'
        || window.getComputedStyle(element).visibility != 'hidden'
        || window.getComputedStyle(element).opacity != '0';
    const isDisabled = element.hasAttribute('disabled');
    return hasTabIndex || (isNaturallyFocusable && isVisible && !isDisabled);
}
/**
 * Registers itself with a parent KeyboardNavigableDirective so it can be focused with arrow keys.
 *
 * Additionally, if the element is not already focusable, adds tabindex="-1" to make it focusable by keyboard.
 */
class FocusableDirective {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
    }
    ngAfterViewInit() {
        const nativeElement = this.el.nativeElement;
        if (!canReceiveFocus(nativeElement)) {
            this.renderer.setAttribute(nativeElement, 'tabindex', '-1');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: FocusableDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.1.1", type: FocusableDirective, isStandalone: true, selector: "[slFocusable]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: FocusableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[slFocusable]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }] });
class KeyboardNavigableConfig {
    constructor(config) {
        //dictates if indexing off one side of the list will wrap to the other side
        this.wraparound = true;
        //determines if navigation should occur with the up/down arrow keys, left/right arrow keys, or both
        this.direction = 'both';
        if (!config)
            return;
        if (typeof config == 'string') {
            throw new Error('Invalid config for KeyboardNavigableDirective');
        }
        else {
            Object.assign(this, config);
        }
    }
}
/**
 * Enables arrow key navigation for all child elements with the `slFocusable` directive.
 *
 * @example <ol [slKeyboardNavigable]="{ wraparound: false, direction: 'horizontal' }">
    <li slFocusable>1</li>
    <li slFocusable>2</li>
    <li slFocusable>3</li>
  </ol>
 */
class KeyboardNavigableDirective {
    constructor() {
        this.currentIndex = 0;
    }
    set keyboardNavigableConfig(value) {
        this.config = new KeyboardNavigableConfig(value);
    }
    handleKeydown(event) {
        const shiftFocus = (direction, offset) => {
            //if the arrow key direction matches the direction we're handling, or if we're handling both directions, trigger the focus shift
            if (this.config.direction == 'both' || this.config.direction == direction) {
                event.preventDefault();
                this.shiftFocus(offset, this.config.wraparound);
            }
        };
        if (event.key == EKeys.ARROW_UP) {
            shiftFocus('vertical', -1);
        }
        else if (event.key == EKeys.ARROW_DOWN) {
            shiftFocus('vertical', 1);
        }
        else if (event.key == EKeys.ARROW_LEFT) {
            shiftFocus('horizontal', -1);
        }
        else if (event.key == EKeys.ARROW_RIGHT) {
            shiftFocus('horizontal', 1);
        }
    }
    ngAfterContentInit() {
        //when the list of focusable items changes, maintain the focus on the new item in the current position
        this.focusableItemsChange$ = this.focusableItems.changes.subscribe(() => this.shiftFocus(0, false));
    }
    ngOnDestroy() {
        this.focusableItemsChange$?.unsubscribe();
    }
    shiftFocus(offset, wraparound) {
        if (!this.focusableItems.length)
            return;
        //focus the item ${offset} items away from the current, wrapping around the list if necessary and if ${wraparound} is true
        if (wraparound) {
            this.currentIndex = (this.currentIndex + offset + this.focusableItems.length) % this.focusableItems.length;
        }
        else {
            this.currentIndex = Math.max(0, Math.min(this.currentIndex + offset, this.focusableItems.length - 1));
        }
        this.focusableItems.toArray()[this.currentIndex].nativeElement.focus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: KeyboardNavigableDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.1.1", type: KeyboardNavigableDirective, isStandalone: true, selector: "[slKeyboardNavigable]", inputs: { keyboardNavigableConfig: ["slKeyboardNavigable", "keyboardNavigableConfig"] }, host: { listeners: { "keydown": "handleKeydown($event)" } }, queries: [{ propertyName: "focusableItems", predicate: FocusableDirective, descendants: true, read: ElementRef }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: KeyboardNavigableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[slKeyboardNavigable]',
                    standalone: true,
                }]
        }], propDecorators: { keyboardNavigableConfig: [{
                type: Input,
                args: ['slKeyboardNavigable']
            }], focusableItems: [{
                type: ContentChildren,
                args: [FocusableDirective, { descendants: true, read: ElementRef }]
            }], handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class SlMenuComponent {
    constructor(hostElement) {
        this.hostElement = hostElement;
        this.items = [];
        this.isOpen = false;
    }
    onDocumentClick(event) {
        if (this.isOpen && !(this.hostElement.nativeElement.contains(event.target))) {
            this.toggleMenu();
        }
    }
    toggleMenu() {
        this.isOpen = !this.isOpen;
    }
    checkIfShouldClose(event) {
        if (event.key === 'Tab' && event.shiftKey && this.isOpen) {
            this.toggleMenu();
        }
    }
    checkIfLast(event, isLast) {
        if (event.key === 'Tab' && isLast) {
            this.toggleMenu();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlMenuComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlMenuComponent, isStandalone: true, selector: "sl-menu", inputs: { name: "name", items: "items" }, host: { listeners: { "document:click": "onDocumentClick($event)" } }, ngImport: i0, template: "<button (click)=\"toggleMenu()\" id=\"new-menu-button\" (keypress.space)=\"toggleMenu()\" [ngClass]=\"{ 'active': isOpen }\" (keydown)=\"checkIfShouldClose($event)\" aria-haspopup=\"true\" aria-controls=\"new-menu\">\n    {{name}}\n</button>\n<ul *ngIf=\"isOpen\" id=\"new-menu\" [slKeyboardNavigable]=\"{ direction: 'vertical' }\" class=\"sub-menu\" role=\"menu\" aria-labelledby=\"new-menu-button\">\n    <ng-container *ngFor=\"let submenuItem of items; last as isLast; first as isFirst\">\n        <li *ngIf=\"submenuItem.hasPermission\" class=\"sub-menu-item\" role=\"presentation\">\n\n            <ng-template [ngIf]=\"!submenuItem.external\" [ngIfElse]=\"subLevelRouterLink\">\n                <a class=\"sub-menu-item\"  role=\"menuitem\" routerLink=\"{{submenuItem.path}}\" (click)=\"toggleMenu()\" (keydown)=\"checkIfLast($event, isLast)\">\n                    <!-- sl-icon currently doesnt work for library components -->\n                    <!-- <sl-icon *ngIf=\"submenuItem.icon\" icon=\"{{submenuItem.icon}}\"></sl-icon> -->\n                    {{submenuItem.linkText}}\n                </a>\n            </ng-template>\n\n            <ng-template #subLevelRouterLink>\n                <a target=\"_blank\"  role=\"menuitem\" class=\"sub-menu-item external-item\" [href]=\"submenuItem.path\" (click)=\"toggleMenu()\" (keydown)=\"checkIfLast($event, isLast)\">\n                    <!-- sl-icon currently doesnt work for library components -->\n                    <!-- <sl-icon *ngIf=\"submenuItem.icon\" icon=\"{{submenuItem.icon}}\"></sl-icon> -->\n                    {{submenuItem.linkText}}\n                    <!-- sl-icon currently doesnt work for library components -->\n                    <!-- <sl-icon icon=\"external-link\"></sl-icon> -->\n                </a>\n            </ng-template>\n\n        </li>\n    </ng-container>\n</ul>\n", styles: [":host{display:block;position:relative;height:100%}.sub-menu{list-style:none;position:absolute;right:0;z-index:100;background-color:#fff;padding:10px;color:#000;display:flex;flex-direction:column;gap:10px;filter:drop-shadow(0 0 1rem rgba(0,0,0,.4))}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: KeyboardNavigableDirective, selector: "[slKeyboardNavigable]", inputs: ["slKeyboardNavigable"] }, { kind: "ngmodule", type: RouterModule }, { kind: "directive", type: i1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-menu', imports: [
                        CommonModule,
                        KeyboardNavigableDirective,
                        RouterModule
                    ], template: "<button (click)=\"toggleMenu()\" id=\"new-menu-button\" (keypress.space)=\"toggleMenu()\" [ngClass]=\"{ 'active': isOpen }\" (keydown)=\"checkIfShouldClose($event)\" aria-haspopup=\"true\" aria-controls=\"new-menu\">\n    {{name}}\n</button>\n<ul *ngIf=\"isOpen\" id=\"new-menu\" [slKeyboardNavigable]=\"{ direction: 'vertical' }\" class=\"sub-menu\" role=\"menu\" aria-labelledby=\"new-menu-button\">\n    <ng-container *ngFor=\"let submenuItem of items; last as isLast; first as isFirst\">\n        <li *ngIf=\"submenuItem.hasPermission\" class=\"sub-menu-item\" role=\"presentation\">\n\n            <ng-template [ngIf]=\"!submenuItem.external\" [ngIfElse]=\"subLevelRouterLink\">\n                <a class=\"sub-menu-item\"  role=\"menuitem\" routerLink=\"{{submenuItem.path}}\" (click)=\"toggleMenu()\" (keydown)=\"checkIfLast($event, isLast)\">\n                    <!-- sl-icon currently doesnt work for library components -->\n                    <!-- <sl-icon *ngIf=\"submenuItem.icon\" icon=\"{{submenuItem.icon}}\"></sl-icon> -->\n                    {{submenuItem.linkText}}\n                </a>\n            </ng-template>\n\n            <ng-template #subLevelRouterLink>\n                <a target=\"_blank\"  role=\"menuitem\" class=\"sub-menu-item external-item\" [href]=\"submenuItem.path\" (click)=\"toggleMenu()\" (keydown)=\"checkIfLast($event, isLast)\">\n                    <!-- sl-icon currently doesnt work for library components -->\n                    <!-- <sl-icon *ngIf=\"submenuItem.icon\" icon=\"{{submenuItem.icon}}\"></sl-icon> -->\n                    {{submenuItem.linkText}}\n                    <!-- sl-icon currently doesnt work for library components -->\n                    <!-- <sl-icon icon=\"external-link\"></sl-icon> -->\n                </a>\n            </ng-template>\n\n        </li>\n    </ng-container>\n</ul>\n", styles: [":host{display:block;position:relative;height:100%}.sub-menu{list-style:none;position:absolute;right:0;z-index:100;background-color:#fff;padding:10px;color:#000;display:flex;flex-direction:column;gap:10px;filter:drop-shadow(0 0 1rem rgba(0,0,0,.4))}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { name: [{
                type: Input
            }], items: [{
                type: Input
            }], onDocumentClick: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }] } });

class SlMultiSelectComponent extends CVAListBase {
    constructor(hostElement) {
        super();
        this.hostElement = hostElement;
        this.placeholder = 'Select any';
        this.isOpen = false;
        this.filteredOptions = [];
        this.form = new FormGroup({
            filterText: new FormControl('')
        });
        this._value = [];
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    ngOnChanges(changes) {
        if (changes['options']) {
            //start listening to changes in the filter
            this.subscription = this.initFilteredOptions().subscribe(list => {
                this.filteredOptions = list;
            });
        }
    }
    /**
     * Responds to clicks outside the host, closing the dropdown
     */
    onDocumentClick(event) {
        if (this.isOpen && !(this.hostElement.nativeElement.contains(event.target))) {
            this.close();
        }
    }
    /**
     * Listens for clicks inside the host and takes action based on the target
     */
    onClick(event) {
        const matchClick = this.isEventTarget.bind(this, event);
        if (matchClick(this.labelElement) && !this.isOpen) {
            //focus the multiselect box
            this.multiselect.nativeElement.focus();
        }
        else if (matchClick(this.multiselect)) {
            //toggle the dropdown visibility
            if (this.isOpen) {
                this.close();
            }
            else {
                this.open();
            }
        }
        event.stopImmediatePropagation();
    }
    /**
     * Listens for key presses inside the host and takes action based on the target and the key
     */
    onKeydown(event) {
        const matchKeydown = this.matchKeydown.bind(this, event);
        if (matchKeydown(this.multiselect, [EKeys.ENTER, EKeys.SPACE])) {
            //prevent page from submiting on enter or scrolling on spacebar
            event.preventDefault();
            this.open();
        }
        else if (matchKeydown(this.multiselect, [EKeys.BACKSPACE])) {
            //deselect last selected option
            if (this.list.length > 0) {
                this.remove(this.list[this.list.length - 1]);
            }
        }
        else if (matchKeydown(this.multiselect, 'any') && !this.isOpen) {
            //open the dropdown and redirect text into the filter element
            if (isAlphanumeric(event.key)) {
                this.open();
                this.form.controls.filterText.patchValue(event.key);
            }
        }
        else if (matchKeydown(this.filter, [EKeys.ESCAPE])) {
            event.stopImmediatePropagation();
            //only close the dropdown if the filter is empty. Otherwise it will naturally clear the filter.
            if (!this.form.controls.filterText.value) {
                this.close();
                this.multiselect.nativeElement.focus();
            }
        }
        else if (matchKeydown(this.filter, [EKeys.ENTER])) {
            event.preventDefault();
            //if only one option, select it
            if (this.filteredOptions.length === 1) {
                this.add(this.filteredOptions[0]);
                this.resetFilter();
            }
        }
        else if (matchKeydown('any', [EKeys.ESCAPE, EKeys.TAB])) {
            this.close();
        }
        else if (matchKeydown('any', [EKeys.SPACE])) {
            //prevent space from scrolling the page
            event.preventDefault();
        }
    }
    open() {
        if (this.isOpen)
            return;
        this.isOpen = true;
        setTimeout(() => {
            this.filter.nativeElement.focus();
        }, 0);
    }
    close() {
        if (!this.isOpen)
            return;
        this.isOpen = false;
        this.resetFilter();
        this.onTouched.forEach(f => f(this.list));
    }
    /**
     * Creates a long-running observable that listens for changes to the filter or the selected options
     * If either changes, the filtered list gets recalculated
     */
    initFilteredOptions() {
        const trimmedFilterText = this.form.get('filterText')?.valueChanges.pipe(startWith(''), map((value) => value?.trim() ?? ''));
        return combineLatest([
            of(this.options),
            trimmedFilterText,
            this.selectionChanges$
        ]).pipe(map(([options, filterText, currentList]) => {
            return this.applyFilter(options, filterText, currentList);
        }));
    }
    /**
     * Calculates which options match the filter text (already selected options are automatically excluded)
     */
    applyFilter(options, filterText, currentList) {
        const response = options.filter((option) => {
            const alreadySelected = currentList.some(o => this.equals(option, o));
            const matchesFilter = !filterText || this.matches(option, filterText);
            return !alreadySelected && matchesFilter;
        });
        return response;
    }
    resetFilter() {
        this.form.controls.filterText.reset();
    }
    /**
     * Determines if the given element was the target of the given event
     */
    isEventTarget(event, element) {
        return element?.nativeElement === event.target;
    }
    /**
     * Determines if a particular key was pressed in the given element
     * @param {KeyboardEvent} event
     * @param {(ElementRef | 'any' | undefined)} matchTarget If undefined, returns false. Otherwise looks for the keypress in this element, or any element if 'any'.
     * @param {(Array<string> | 'any')} keys Matches the keypress against this list; always matches if 'any'.
     */
    matchKeydown(event, matchTarget, keys) {
        return (matchTarget == 'any' || this.isEventTarget(event, matchTarget))
            && (keys == 'any' || keys.includes(event.key));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlMultiSelectComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlMultiSelectComponent, isStandalone: true, selector: "sl-multiselect", inputs: { label: "label", placeholder: "placeholder", equals: ["compareWith", "equals"], matches: ["filterBy", "matches"], options: "options" }, host: { listeners: { "document:click": "onDocumentClick($event)", "click": "onClick($event)", "keydown": "onKeydown($event)" }, properties: { "class.hasError": "this.errorMessage" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SlMultiSelectComponent),
                multi: true,
            },
        ], queries: [{ propertyName: "itemTemplate", first: true, predicate: TemplateRef, descendants: true }], viewQueries: [{ propertyName: "labelElement", first: true, predicate: ["labelElement"], descendants: true }, { propertyName: "multiselect", first: true, predicate: ["multiselect"], descendants: true }, { propertyName: "dropdown", first: true, predicate: ["dropdown"], descendants: true }, { propertyName: "filter", first: true, predicate: ["filter"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<ng-container [formGroup]=\"form\">\n  <label #labelElement [for]=\"identifier\" *ngIf=\"label\">{{ label }}</label>\n\n  <ol #multiselect class=\"multiselect\" [ngClass]=\"{ 'open': isOpen }\" role=\"listbox\" tabindex=\"0\" aria-autocomplete=\"list\" aria-haspopup=\"true\" aria-controls=\"dropdown\" aria-label=\"selected options\" [attr.aria-expanded]=\"isOpen\"><!-- [slKeyboardNavigable]=\"{ direction: 'horizontal' }\" -->\n    <li class=\"placeholder\" *ngIf=\"!list?.length\">{{ placeholder }}</li>\n\n    <li class=\"pill\" role=\"option\" aria-selected=\"true\" tabindex=\"0\" *ngFor=\"let item of list\"><!-- slFocusable -->\n      <ng-container *ngTemplateOutlet=\"itemTemplate || defaultTemplate; context: { $implicit: item }\"></ng-container>\n      \n      <button type=\"button\" class=\"remove-button\" aria-label=\"click to remove\" (click)=\"remove(item)\" (keydown.enter)=\"remove(item)\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"\n            stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n            class=\"feather feather-x\">\n            <line x1=\"24\" y1=\"0\" x2=\"0\" y2=\"24\"></line>\n            <line x1=\"0\" y1=\"0\" x2=\"24\" y2=\"24\"></line>\n        </svg>\n      </button>\n    </li>\n  </ol>\n\n  <ul [id]=\"identifier\" #dropdown class=\"dropdown\" role=\"listbox\" *ngIf=\"isOpen\" [slKeyboardNavigable]=\"{ direction: 'vertical' }\">\n    <li>\n      <input #filter type=\"search\" placeholder=\"type to filter\" aria-label=\"Filter options\" formControlName=\"filterText\" slFocusable>\n    </li>\n\n    <ng-container *ngFor=\"let item of filteredOptions\">\n      <li class=\"option\" role=\"option\" aria-selected=\"false\" (click)=\"add(item)\" (keydown.enter)=\"add(item)\" slFocusable>\n        <ng-container *ngTemplateOutlet=\"itemTemplate || defaultTemplate; context: { $implicit: item }\"></ng-container>\n      </li>\n    </ng-container>\n    \n    <li class=\"placeholder\" *ngIf=\"!filteredOptions.length\">No results</li>\n  </ul>\n\n  <span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n    <ng-content select=\"sl-validation-error-message\"></ng-content>\n</span>\n</ng-container>\n\n<ng-template #defaultTemplate let-item>{{ item }}</ng-template>\n", styles: ["ol,ul{list-style-type:none;display:flex}.multiselect{flex-flow:row wrap;align-items:center;cursor:pointer;position:relative}.multiselect .pill{cursor:default}.multiselect .placeholder{pointer-events:none}.multiselect .remove-button{line-height:0;background:none;border:none;vertical-align:middle;cursor:pointer}.dropdown{flex-flow:column nowrap;position:absolute;z-index:1;overflow:visible}.dropdown .option{cursor:pointer}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: FocusableDirective, selector: "[slFocusable]" }, { kind: "directive", type: KeyboardNavigableDirective, selector: "[slKeyboardNavigable]", inputs: ["slKeyboardNavigable"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlMultiSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-multiselect', imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        FocusableDirective,
                        KeyboardNavigableDirective,
                    ], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SlMultiSelectComponent),
                            multi: true,
                        },
                    ], template: "<ng-container [formGroup]=\"form\">\n  <label #labelElement [for]=\"identifier\" *ngIf=\"label\">{{ label }}</label>\n\n  <ol #multiselect class=\"multiselect\" [ngClass]=\"{ 'open': isOpen }\" role=\"listbox\" tabindex=\"0\" aria-autocomplete=\"list\" aria-haspopup=\"true\" aria-controls=\"dropdown\" aria-label=\"selected options\" [attr.aria-expanded]=\"isOpen\"><!-- [slKeyboardNavigable]=\"{ direction: 'horizontal' }\" -->\n    <li class=\"placeholder\" *ngIf=\"!list?.length\">{{ placeholder }}</li>\n\n    <li class=\"pill\" role=\"option\" aria-selected=\"true\" tabindex=\"0\" *ngFor=\"let item of list\"><!-- slFocusable -->\n      <ng-container *ngTemplateOutlet=\"itemTemplate || defaultTemplate; context: { $implicit: item }\"></ng-container>\n      \n      <button type=\"button\" class=\"remove-button\" aria-label=\"click to remove\" (click)=\"remove(item)\" (keydown.enter)=\"remove(item)\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"\n            stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"\n            class=\"feather feather-x\">\n            <line x1=\"24\" y1=\"0\" x2=\"0\" y2=\"24\"></line>\n            <line x1=\"0\" y1=\"0\" x2=\"24\" y2=\"24\"></line>\n        </svg>\n      </button>\n    </li>\n  </ol>\n\n  <ul [id]=\"identifier\" #dropdown class=\"dropdown\" role=\"listbox\" *ngIf=\"isOpen\" [slKeyboardNavigable]=\"{ direction: 'vertical' }\">\n    <li>\n      <input #filter type=\"search\" placeholder=\"type to filter\" aria-label=\"Filter options\" formControlName=\"filterText\" slFocusable>\n    </li>\n\n    <ng-container *ngFor=\"let item of filteredOptions\">\n      <li class=\"option\" role=\"option\" aria-selected=\"false\" (click)=\"add(item)\" (keydown.enter)=\"add(item)\" slFocusable>\n        <ng-container *ngTemplateOutlet=\"itemTemplate || defaultTemplate; context: { $implicit: item }\"></ng-container>\n      </li>\n    </ng-container>\n    \n    <li class=\"placeholder\" *ngIf=\"!filteredOptions.length\">No results</li>\n  </ul>\n\n  <span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n    <ng-content select=\"sl-validation-error-message\"></ng-content>\n</span>\n</ng-container>\n\n<ng-template #defaultTemplate let-item>{{ item }}</ng-template>\n", styles: ["ol,ul{list-style-type:none;display:flex}.multiselect{flex-flow:row wrap;align-items:center;cursor:pointer;position:relative}.multiselect .pill{cursor:default}.multiselect .placeholder{pointer-events:none}.multiselect .remove-button{line-height:0;background:none;border:none;vertical-align:middle;cursor:pointer}.dropdown{flex-flow:column nowrap;position:absolute;z-index:1;overflow:visible}.dropdown .option{cursor:pointer}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { errorMessage: [{
                type: HostBinding,
                args: ['class.hasError']
            }], label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], equals: [{
                type: Input,
                args: ['compareWith']
            }], matches: [{
                type: Input,
                args: ['filterBy']
            }], options: [{
                type: Input
            }], itemTemplate: [{
                type: ContentChild,
                args: [TemplateRef]
            }], labelElement: [{
                type: ViewChild,
                args: ['labelElement']
            }], multiselect: [{
                type: ViewChild,
                args: ['multiselect']
            }], dropdown: [{
                type: ViewChild,
                args: ['dropdown']
            }], filter: [{
                type: ViewChild,
                args: ['filter']
            }], onDocumentClick: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class SlRadioComponent extends CVABase {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlRadioComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlRadioComponent, isStandalone: true, selector: "sl-radio", inputs: { name: "name", label: "label", val: "val", id: "id", ariaLabel: "ariaLabel" }, host: { properties: { "class.hasError": "this.errorMessage" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SlRadioComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<label class=\"sl-radio\" [for]=\"identifier\">\n  <input\n    type=\"radio\"\n    [name]=\"name\"\n    [id]=\"identifier\"\n    [disabled]=\"disabled\"\n    [value]=\"val\"\n    [(ngModel)]=\"value\"\n    [attr.aria-label]=\"ariaLabel\"\n  />\n  <span class=\"radio\"></span>\n  {{ label }}\n</label>\n<span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n  <ng-content select=\"sl-validation-error-message\"></ng-content>\n</span>\n", styles: [":host{--primary: #eb0808;--accent: #2d00f3;--border: #da26c2}input{position:absolute;height:.875rem;width:.875rem;z-index:-1}.sl-radio{position:relative}.radio{width:1rem;height:1rem;border:solid .0625rem var(--primary);background-color:#fff;display:inline-flex;justify-content:center;align-items:center;cursor:pointer;margin-right:.1875rem;border-radius:100%;position:relative;transition:border-color .2s linear}.radio:hover{background:var(--primary);border-color:var(--border)}.radio:focus{background:var(--primary);border-color:var(--border)}input:disabled+.radio{background:var(--primary);border-color:var(--border);cursor:default}label{display:inline-flex;flex-direction:row;align-content:center;align-items:center;gap:.1875rem}.radio:after{content:\"\";position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) scale(0);transform-origin:50% 50%;width:10px;height:10px;border-radius:100%;background:var(--accent)}input:checked+.radio{border-color:var(--accent)}input:checked+.radio:after{animation:.2s cubic-bezier(1,.63,.06,.32) 0s forwards radioDotAnimate}.error-message{font-size:.75rem;margin-top:.3125rem}@keyframes radioDotAnimate{to{transform:translate(-50%,-50%) scale(1)}}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlRadioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-radio', imports: [CommonModule, FormsModule], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SlRadioComponent),
                            multi: true,
                        },
                    ], template: "<label class=\"sl-radio\" [for]=\"identifier\">\n  <input\n    type=\"radio\"\n    [name]=\"name\"\n    [id]=\"identifier\"\n    [disabled]=\"disabled\"\n    [value]=\"val\"\n    [(ngModel)]=\"value\"\n    [attr.aria-label]=\"ariaLabel\"\n  />\n  <span class=\"radio\"></span>\n  {{ label }}\n</label>\n<span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n  <ng-content select=\"sl-validation-error-message\"></ng-content>\n</span>\n", styles: [":host{--primary: #eb0808;--accent: #2d00f3;--border: #da26c2}input{position:absolute;height:.875rem;width:.875rem;z-index:-1}.sl-radio{position:relative}.radio{width:1rem;height:1rem;border:solid .0625rem var(--primary);background-color:#fff;display:inline-flex;justify-content:center;align-items:center;cursor:pointer;margin-right:.1875rem;border-radius:100%;position:relative;transition:border-color .2s linear}.radio:hover{background:var(--primary);border-color:var(--border)}.radio:focus{background:var(--primary);border-color:var(--border)}input:disabled+.radio{background:var(--primary);border-color:var(--border);cursor:default}label{display:inline-flex;flex-direction:row;align-content:center;align-items:center;gap:.1875rem}.radio:after{content:\"\";position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) scale(0);transform-origin:50% 50%;width:10px;height:10px;border-radius:100%;background:var(--accent)}input:checked+.radio{border-color:var(--accent)}input:checked+.radio:after{animation:.2s cubic-bezier(1,.63,.06,.32) 0s forwards radioDotAnimate}.error-message{font-size:.75rem;margin-top:.3125rem}@keyframes radioDotAnimate{to{transform:translate(-50%,-50%) scale(1)}}\n"] }]
        }], propDecorators: { errorMessage: [{
                type: HostBinding,
                args: ['class.hasError']
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }], val: [{
                type: Input
            }], id: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }] } });

class SlTextareaComponent extends CVABase {
    constructor() {
        super(...arguments);
        this.hasError = false;
        this.useLabelAsHeader = false;
        this.stripHtmlTags = true;
        this.rows = 10;
        this.columns = 30;
        this.placeholder = '';
        this.required = false;
        this.charCount = 0;
        this.wordCount = 0;
        this.charCountMessage = 'characters allowed';
        this.overCharLimit = false;
        this.closeToCharLimit = false;
        this.showWarnings = false;
    }
    ngOnInit() {
        this.charRemaining = this.maxLength - this.charCount;
    }
    writeValue(value) {
        this._value = value;
        this.counter(this._value);
    }
    resetToDefaults() {
        this.charRemaining = this.maxLength;
        this.closeToCharLimit = false;
        this.overCharLimit = false;
        this.charCountMessage = 'characters allowed';
    }
    inputCounter(e) {
        const countTarget = e.target;
        this.counter(countTarget.value);
    }
    counter(s) {
        if (!s) {
            this.resetToDefaults();
            return;
        }
        this.charCount = s.length;
        this.wordCount = s.split(/\s+/).length;
        if (this.charCount > this.maxLength) {
            this.closeToCharLimit = false;
            this.overCharLimit = true;
            this.charRemaining = (this.maxLength - this.charCount) * -1;
            this.charCountMessage = 'characters over limit';
            if (this.charRemaining === 1) {
                this.charCountMessage = 'character over limit';
            }
        }
        else if ((this.maxLength - this.charCount) < 21) {
            this.closeToCharLimit = true;
            this.charRemaining = this.maxLength - this.charCount;
            this.charCountMessage = 'characters left';
        }
        else {
            this.overCharLimit = false;
            this.closeToCharLimit = false;
            this.charRemaining = this.maxLength - this.charCount;
            this.charCountMessage = 'characters left';
        }
    }
    markAsTouched() {
        this.onTouched.forEach(f => f(this.value));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlTextareaComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlTextareaComponent, isStandalone: true, selector: "sl-textarea", inputs: { name: "name", label: "label", hasError: "hasError", useLabelAsHeader: "useLabelAsHeader", stripHtmlTags: "stripHtmlTags", rows: "rows", columns: "columns", placeholder: "placeholder", showCount: "showCount", maxLength: "maxLength", required: "required", showWarnings: "showWarnings" }, host: { properties: { "class.hasError": "this.hasError" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SlTextareaComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<label [ngClass]=\"{'like-h4': useLabelAsHeader}\" *ngIf=\"label\" [for]=\"identifier\" [innerHTML]=\"label\"></label>\n<div class=\"lu-textarea\">\n  <p *ngIf=\"hasError\" class=\"error-message\"><ng-content></ng-content></p>\n  <textarea [attr.aria-label]=\"required\"\n    [attr.aria-invalid]=\"hasError\"\n    [id]=\"identifier\"\n    [cols]=\"columns\"\n    [rows]=\"rows\"\n    (input)=\"inputCounter($event)\"\n    [name]=\"name\"\n    maxlength=\"{{maxLength}}\"\n    [placeholder]=\"placeholder\"\n    [disabled]=\"disabled\"\n    [(ngModel)]=\"value\"\n    (blur)=\"markAsTouched()\"\n  ></textarea>\n  <p\n    *ngIf=\"showCount\"\n    class=\"character-counter\"\n    [class.over-limit]=\"overCharLimit\"\n    [class.close-to-limit]=\"closeToCharLimit\"\n    aria-live=\"polite\"\n  >\n    {{ charRemaining }} {{ charCountMessage }}\n  </p>\n</div>\n<span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n  <ng-content select=\"sl-validation-error-message\"></ng-content>\n</span>\n", styles: [":host{display:inline-block;width:100%;max-width:65ch}textarea{border:1px solid #D4D6D7;width:100%;padding:rem-calc(15px)}.over-limit{font-weight:700;color:#ff534d}.close-to-limit{font-weight:700;color:#f5b939}.error-message{color:#ff534d;margin-top:rem-calc(12px);margin-bottom:rem-calc(5px)}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlTextareaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-textarea', imports: [CommonModule, FormsModule], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SlTextareaComponent),
                            multi: true,
                        },
                    ], template: "<label [ngClass]=\"{'like-h4': useLabelAsHeader}\" *ngIf=\"label\" [for]=\"identifier\" [innerHTML]=\"label\"></label>\n<div class=\"lu-textarea\">\n  <p *ngIf=\"hasError\" class=\"error-message\"><ng-content></ng-content></p>\n  <textarea [attr.aria-label]=\"required\"\n    [attr.aria-invalid]=\"hasError\"\n    [id]=\"identifier\"\n    [cols]=\"columns\"\n    [rows]=\"rows\"\n    (input)=\"inputCounter($event)\"\n    [name]=\"name\"\n    maxlength=\"{{maxLength}}\"\n    [placeholder]=\"placeholder\"\n    [disabled]=\"disabled\"\n    [(ngModel)]=\"value\"\n    (blur)=\"markAsTouched()\"\n  ></textarea>\n  <p\n    *ngIf=\"showCount\"\n    class=\"character-counter\"\n    [class.over-limit]=\"overCharLimit\"\n    [class.close-to-limit]=\"closeToCharLimit\"\n    aria-live=\"polite\"\n  >\n    {{ charRemaining }} {{ charCountMessage }}\n  </p>\n</div>\n<span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n  <ng-content select=\"sl-validation-error-message\"></ng-content>\n</span>\n", styles: [":host{display:inline-block;width:100%;max-width:65ch}textarea{border:1px solid #D4D6D7;width:100%;padding:rem-calc(15px)}.over-limit{font-weight:700;color:#ff534d}.close-to-limit{font-weight:700;color:#f5b939}.error-message{color:#ff534d;margin-top:rem-calc(12px);margin-bottom:rem-calc(5px)}\n"] }]
        }], propDecorators: { errorMessage: [{
                type: HostBinding,
                args: ['class.hasError']
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }], hasError: [{
                type: HostBinding,
                args: ['class.hasError']
            }, {
                type: Input
            }], useLabelAsHeader: [{
                type: Input
            }], stripHtmlTags: [{
                type: Input
            }], rows: [{
                type: Input
            }], columns: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], showCount: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], required: [{
                type: Input
            }], showWarnings: [{
                type: Input
            }] } });

class FormDebuggerComponent {
    constructor() {
        this.showValues = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: FormDebuggerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: FormDebuggerComponent, isStandalone: true, selector: "lu-form-debugger", inputs: { form: "form", showValues: "showValues" }, ngImport: i0, template: "<h2>Form Debugger: form is {{ form.valid ? 'valid' : 'NOT valid' }}</h2>\n\n<table aria-hidden=\"true\">\n  <thead>\n    <th scope=\"col\">Control</th>\n    <th scope=\"col\">Valid?</th>\n    <th scope=\"col\">Dirty?</th>\n    <th scope=\"col\">Touched?</th>\n    <th scope=\"col\">Disabled?</th>\n    <th scope=\"col\">Error(s)</th>\n    <th scope=\"col\" *ngIf=\"showValues\">Value</th>\n  </thead>\n  \n  <!-- NOTE: this currently doesn't support nested form groups -->\n  <tr *ngFor=\"let control of form.controls | keyvalue\">\n    <th scope=\"row\">{{ control.key }}</th>\n    <td>{{ control.value.valid ? 'Y' : 'N' }}</td>\n    <td>{{ control.value.dirty ? 'Y' : 'N' }}</td>\n    <td>{{ control.value.touched ? 'Y' : 'N' }}</td>\n    <td>{{ control.value.disabled ? 'Y' : 'N' }}</td>\n    <td>{{ control.value.errors | json }}</td>\n    <td *ngIf=\"showValues\"><pre>{{ control.value.value | json }}</pre></td>\n  </tr>\n\n  <tfoot>\n    <th scope=\"row\">Form</th>\n    <td>{{ form.valid ? 'Y' : 'N' }}</td>\n    <td>{{ form.dirty ? 'Y' : 'N' }}</td>\n    <td>{{ form.touched ? 'Y' : 'N' }}</td>\n    <td>{{ form.disabled ? 'Y' : 'N' }}</td>\n    <td>{{ form.errors | json }}</td>\n    <td *ngIf=\"showValues\">n/a</td>\n  </tfoot>\n</table>\n", styles: [":host{display:block}:host td,:host th{border:1px solid black;padding:4px 8px}:host th{font-weight:500}:host tr th{text-align:left}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1$1.JsonPipe, name: "json" }, { kind: "pipe", type: i1$1.KeyValuePipe, name: "keyvalue" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: FormDebuggerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lu-form-debugger', imports: [CommonModule], template: "<h2>Form Debugger: form is {{ form.valid ? 'valid' : 'NOT valid' }}</h2>\n\n<table aria-hidden=\"true\">\n  <thead>\n    <th scope=\"col\">Control</th>\n    <th scope=\"col\">Valid?</th>\n    <th scope=\"col\">Dirty?</th>\n    <th scope=\"col\">Touched?</th>\n    <th scope=\"col\">Disabled?</th>\n    <th scope=\"col\">Error(s)</th>\n    <th scope=\"col\" *ngIf=\"showValues\">Value</th>\n  </thead>\n  \n  <!-- NOTE: this currently doesn't support nested form groups -->\n  <tr *ngFor=\"let control of form.controls | keyvalue\">\n    <th scope=\"row\">{{ control.key }}</th>\n    <td>{{ control.value.valid ? 'Y' : 'N' }}</td>\n    <td>{{ control.value.dirty ? 'Y' : 'N' }}</td>\n    <td>{{ control.value.touched ? 'Y' : 'N' }}</td>\n    <td>{{ control.value.disabled ? 'Y' : 'N' }}</td>\n    <td>{{ control.value.errors | json }}</td>\n    <td *ngIf=\"showValues\"><pre>{{ control.value.value | json }}</pre></td>\n  </tr>\n\n  <tfoot>\n    <th scope=\"row\">Form</th>\n    <td>{{ form.valid ? 'Y' : 'N' }}</td>\n    <td>{{ form.dirty ? 'Y' : 'N' }}</td>\n    <td>{{ form.touched ? 'Y' : 'N' }}</td>\n    <td>{{ form.disabled ? 'Y' : 'N' }}</td>\n    <td>{{ form.errors | json }}</td>\n    <td *ngIf=\"showValues\">n/a</td>\n  </tfoot>\n</table>\n", styles: [":host{display:block}:host td,:host th{border:1px solid black;padding:4px 8px}:host th{font-weight:500}:host tr th{text-align:left}\n"] }]
        }], propDecorators: { form: [{
                type: Input
            }], showValues: [{
                type: Input
            }] } });

/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/**
 * This class can be provided in place of HttpClient in order to
 * prevent PUT, POST, or DELETE requests from being sent to the API.
 *
 * Prompts the user to optionally provide fake a JSON response for each request.
 *
 * @example { provide: HttpClient, useClass: ReadOnlyHttpClient }
 *
 * @export
 * @class ReadOnlyHttpClient
 * @extends {HttpClient}
 */
class ReadOnlyHttpClient extends HttpClient {
    put(url, body, options) {
        return this.fakeHttpRequest('PUT', url, body, options);
    }
    post(url, body, options) {
        return this.fakeHttpRequest('POST', url, body, options);
    }
    delete(url, options) {
        return this.fakeHttpRequest('DELETE', url, null, options);
    }
    fakeHttpRequest(verb, url, body, options) {
        return new Observable(observer => {
            console.log(`${verb} ${url}`, body, options);
            const result = this.getUserInput(`${verb} ${url}\n\nEnter JSON response or cancel for null`);
            console.log(`returning:`, result);
            observer.next(result);
            observer.complete();
        });
    }
    getUserInput(message) {
        const userInput = window.prompt(message);
        if (userInput == null)
            return null;
        try {
            //if user canceled or left input blank, just use null. otherwise, attempt to parse JSON
            // @ts-ignore to disable implicit any error for this line
            return this[`(${userInput})`]; //this works with a looser syntax vs JSON.parse
        }
        catch {
            return null;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ReadOnlyHttpClient, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ReadOnlyHttpClient }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ReadOnlyHttpClient, decorators: [{
            type: Injectable
        }] });

var EMode;
(function (EMode) {
    EMode["YES"] = "YES";
    EMode["NO"] = "NO";
    EMode["UNSURE"] = "UNSURE";
    EMode["STUDENT"] = "STUDENT";
    EMode["FACULTY"] = "FACULTY";
    EMode["STAFF"] = "STAFF";
    EMode["VIEW"] = "VIEW";
    EMode["NEW"] = "NEW";
    EMode["EDIT"] = "EDIT";
    EMode["DELETE"] = "DELETE";
    EMode["SEARCH"] = "SEARCH";
})(EMode || (EMode = {}));

class ModeGroup {
    static { this.YES_NO_UNSURE = [EMode.YES, EMode.NO, EMode.UNSURE]; }
    static { this.STUDENT_STAFF = [EMode.STUDENT, EMode.STAFF]; }
    static { this.FACULTY_STAFF = [EMode.FACULTY, EMode.STAFF]; }
    static { this.VIEW_EDIT = [EMode.VIEW, EMode.EDIT]; }
    static { this.VIEW_NEW_EDIT = [EMode.VIEW, EMode.NEW, EMode.EDIT]; }
    static { this.VIEW_NEW_EDIT_DELETE = [EMode.VIEW, EMode.NEW, EMode.EDIT, EMode.DELETE]; }
    static { this.VIEW_NEW_EDIT_DELETE_SEARCH = [EMode.VIEW, EMode.NEW, EMode.EDIT, EMode.DELETE, EMode.SEARCH]; }
}

/**
 * Track and manage state between different mode values
 *
 * @abstract
 * @class Modes
 */
class Modes {
    constructor(modes, defaultMode) {
        this.modes = modes;
        this.defaultMode = defaultMode;
        // if no default mode provided, use the first one
        this.current = this.defaultMode || this.modes[0];
    }
    get current() {
        return this.modes[this.currentIndex];
    }
    set current(mode) {
        this.validate(mode);
        this.currentIndex = (mode) ? this.modes.indexOf(mode) : 0;
    }
    /**
     * Returns true if the current mode matches the parameter
     *
     * @param {EMode} mode
     * @returns {boolean}
     */
    is(mode) {
        this.validate(mode);
        return this.current == mode;
    }
    /**
     * Advances the current mode to the next in the array, looping around as necessary
     */
    toggleNext() {
        this.currentIndex++;
        this.currentIndex %= this.modes.length;
    }
    /**
     * Throws an error if the given mode isn't in the configured array of options
     *
     * @private
     * @param {EMode} mode
     */
    validate(mode) {
        if (this.modes.indexOf(mode) < 0)
            throw new Error(`Mode "${mode}" is undefined!`);
    }
}
/**
 * Following here are subclasses of Modes that specify a particular set of mode options.
 *
 * These are the classes that should be used in components, _not_ the Modes class above.
 */
class YesNoUnsureModes extends Modes {
    constructor(defaultMode) {
        super(ModeGroup.YES_NO_UNSURE, defaultMode);
    }
}
class StudentStaffModes extends Modes {
    constructor(defaultMode) {
        super(ModeGroup.STUDENT_STAFF, defaultMode);
    }
}
class FacultyStaffModes extends Modes {
    constructor(defaultMode) {
        super(ModeGroup.FACULTY_STAFF, defaultMode);
    }
}
class ViewEditModes extends Modes {
    constructor(defaultMode) {
        super(ModeGroup.VIEW_EDIT, defaultMode);
    }
}
class ViewNewEditModes extends Modes {
    constructor(defaultMode) {
        super(ModeGroup.VIEW_NEW_EDIT, defaultMode);
    }
}
class ViewNewEditDeleteModes extends Modes {
    constructor(defaultMode) {
        super(ModeGroup.VIEW_NEW_EDIT_DELETE, defaultMode);
    }
}
class ViewNewEditDeleteSearchModes extends Modes {
    constructor(defaultMode) {
        super(ModeGroup.VIEW_NEW_EDIT_DELETE_SEARCH, defaultMode);
    }
}

/**
  This class provides the base functionality for an editable widget on the IR Profile.

  The widget MUST implement view and edit modes.

  In view mode, the widget MUST render a button bound to the {@link edit} method.
  In edit mode, the widget MUST render buttons bound to the {@link cancel} and {@link submit} methods.
  The submit button SHOULD be disabled when the {@link submitting} property is true.

  The widget MUST provide an implementation for the {@link save} method, which handles any necessary service interactions.

  The widget MAY override the {@link onSuccess} method to perform custom behavior after saving.
 *
 * @export
 * @abstract
 * @class Editable
 */
class Editable {
    constructor(formResetAllowed = true) {
        this.formResetAllowed = formResetAllowed;
        this.modes = new ViewEditModes();
        this.submitting = false;
    }
    /**
     * Saves changes.
     *
     * If there were no changes, simply resets the component back to view mode.
     * If the form is valid, calls the subclass's {@link save} method to perform any necessary actions, then resets the component back to view mode.
     * If the form is invalid, marks all controls as dirty (to trigger validation messages) and leaves the component in edit mode.
     */
    submit() {
        // if already submitted, then do not attempt again
        if (this.submitting) {
            return;
        }
        // if the user didn't interact with the form, there's nothing to save, so just reset.
        if (this.form.pristine) {
            this.reset();
            return;
        }
        this.markAllDirty();
        if (this.form.invalid)
            return;
        this.submitting = true;
        this.save().subscribe(() => {
            this.reset();
            this.onSuccess();
        }).add(() => {
            this.submitting = false;
        });
    }
    /**
     * Returns true if the component is currently in edit mode.
     */
    isEdit() {
        return this.modes.is(EMode.EDIT);
    }
    /**
     * Switches the component into edit mode.
     * Invokes {@link initForm} which may perform setup logic.
     */
    edit() {
        this.modes.current = EMode.EDIT;
        this.initForm();
    }
    /**
     * Resets the form and switches the component into view mode.
     */
    cancel() {
        this.reset();
        this.onCancel();
    }
    /**
     * Resets the form. Called after submission or when the user cancels.
     */
    reset() {
        this.modes.current = EMode.VIEW;
        if (this.formResetAllowed) {
            this.form.reset();
        }
    }
    /**
     * This hook is called after submission completes.
     * Subclasses MAY override this method.
     */
    onSuccess() { } // eslint-disable-line @typescript-eslint/no-empty-function
    /**
     * This hook is called after submission is cancelled.
     * Subclasses MAY override this method.
     */
    onCancel() { } // eslint-disable-line @typescript-eslint/no-empty-function
    /**
     * This hook is called when switching to edit mode.
     * Subclasses MAY override this method
     */
    initForm() { } // eslint-disable-line @typescript-eslint/no-empty-function
    /**
     * Updates all form controls to dirty, triggering their validation messages to be shown.
     */
    markAllDirty() {
        Object.values(this.form.controls).forEach(control => {
            control.markAsDirty();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: Editable, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.1.1", type: Editable, isStandalone: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: Editable, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: undefined }] });

class EditableList extends Editable {
    constructor() {
        super(...arguments);
        // list of original items used for view context
        this.items = [];
        // list of items having state used for edit context
        this.statefulItems = [];
    }
    /**
     * Adds a item whose values are generated from form controls, marking it for addition later when form changes are submitted.
     *
     * The generated item is added to the list of items for the edit context and the form is reset.
     */
    add() {
        this.markAllDirty();
        if (this.form.invalid)
            return;
        const newItem = {
            status: 'A',
            value: this.buildItem()
        };
        this.statefulItems.push({ ...newItem });
        this.form.reset();
    }
    /**
     * Marks the specified item at the given index for deletion later when form changes are submitted.
     *
     * @param index the index of the edit item to be deleted
     */
    delete(index) {
        if (!(index in this.statefulItems))
            return;
        if (this.statefulItems[index].status === 'A') {
            // item was recently added, but not posted, so simply remove it from the list of items for the edit context
            this.statefulItems.splice(index, 1);
        }
        else {
            // must be an existing item that has been posted, so only mark the item for deletion upon save
            this.statefulItems[index].status = 'D';
        }
    }
    /**
     * Saves changes.
     *
     * Overrides the submit method in Editable in order to not check the form status before calling {@link save}
     */
    submit() {
        this.submitting = true;
        this.save().subscribe(() => {
            this.cancel();
            this.onSuccess();
        }).add(() => {
            this.submitting = false;
        });
    }
    /**
     * Submits the accumulated edit changes for items marked for addition and/or deletion.
     *
     * Any errors detected will cause the component to remain in the edit context, otherwise the
     * context is switched to view and the form is reset.
     */
    save() {
        // handle deletions, first filtering and mapping stateful items to normal items and then submitting.
        const deleteObservables = this.statefulItems
            .filter((item) => item.status === 'D')
            .map(item => {
            return this.doDelete(item.value).pipe(tap(() => {
                // Keep view / edit mode arrays in sync
                this.removeItem(this.items, item.value);
                this.removeItem(this.statefulItems, item.value);
            }));
        });
        // handle additions, first filtering and mapping stateful items to normal items and then submitting.
        const addObservables = this.statefulItems
            .filter((item) => item.status === 'A')
            .map(item => {
            return this.doAdd(item.value).pipe(tap((f) => {
                item.value.id = f.id;
                // Keep view / edit mode arrays in sync
                this.items.push(item.value);
            }));
        });
        return forkJoin([...deleteObservables, ...addObservables]).pipe(defaultIfEmpty([]) //make sure to emit something if there were no changes
        );
    }
    /**
     * Removes a item from a specified list of items where the items have a context stemming from view or edit mode.
     *
     * @param items the list of items to remove from
     * @param item the item to remove
     */
    removeItem(list, item) {
        const index = list.findIndex((f) => {
            // if f is a IStatefulItem, we have to reach in to get its value
            return (('id' in f) ? f : f.value).id == item.id;
        });
        if (index !== -1) {
            list.splice(index, 1);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: EditableList, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.1.1", type: EditableList, isStandalone: true, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: EditableList, decorators: [{
            type: Directive
        }] });

var EModalType;
(function (EModalType) {
    EModalType["alert"] = "alert";
    EModalType["toast"] = "toast";
    EModalType["confirm"] = "confirm";
    EModalType["custom"] = "custom";
})(EModalType || (EModalType = {}));

/**
 * Builds the query params for an HTTP request, gracefully handling optional parameters.
 *
 * For a given set of key-value pairs (where the values are potentially undefined),
 * returns a wrapped HttpParams object with all undefined values excluded.
 *
 * @export
 * @param {{ [paramName: string]: ParamTypes }} possiblyUndefinedParams
 * @return {*}  {{ params: HttpParams }} An HttpParams object wrapped into a valid {@link HttpClient.get} options object
 */
function withParams(possiblyUndefinedParams) {
    // for the set of key-value pairs, we reduce them into a new HttpErrors object, but only add the ones that have a defined value
    const coalescedParams = Object.entries(possiblyUndefinedParams).reduce((collectedParams, [paramName, paramValue]) => {
        return (paramValue != undefined) ? collectedParams.set(paramName, paramValue) : collectedParams;
    }, new HttpParams());
    return { params: coalescedParams };
}

class AbstractUserService {
}

class SlFormModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlFormModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.1", ngImport: i0, type: SlFormModule, imports: [SlInputComponent,
            SlDatetimeComponent], exports: [SlInputComponent,
            SlDatetimeComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlFormModule, imports: [SlInputComponent,
            SlDatetimeComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlFormModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        SlInputComponent,
                        SlDatetimeComponent
                    ],
                    exports: [
                        SlInputComponent,
                        SlDatetimeComponent
                    ]
                }]
        }] });

class FileSizePipe {
    constructor() {
        this.byteUnits = [' kB', ' MB', ' GB'];
    }
    transform(origSize) {
        let size = origSize;
        if (size < 1024) {
            return String(size) + ' bytes';
        }
        let i = -1;
        while (size > 1023) {
            size = size / 1024;
            i = i + 1;
        }
        return Math.max(size, 0.1).toFixed(1) + this.byteUnits[i];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: FileSizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.1.1", ngImport: i0, type: FileSizePipe, isStandalone: true, name: "fileSize" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: FileSizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fileSize',
                    standalone: true,
                }]
        }] });

class OrderByPipe {
    /* eslint-disable  @typescript-eslint/no-unsafe-member-access */
    /* eslint-disable  @typescript-eslint/no-unsafe-return */
    transform(array, propertyName, reverse) {
        if (!Array.isArray(array)) {
            return array;
        }
        array.sort((a, b) => {
            if (this.getProp(a, propertyName) < this.getProp(b, propertyName)) {
                return -1;
            }
            else if (this.getProp(a, propertyName) > this.getProp(b, propertyName)) {
                return 1;
            }
            else {
                return 0;
            }
        });
        if (reverse) {
            return array.reverse();
        }
        else {
            return array;
        }
    }
    /** Get a nested property from an object without returning any errors.
   * If the property or property chain doesn't exist, undefined is returned.
   * Property names with spaces may use either dot or bracket "[]" notation.
   * Note that bracketed property names without surrounding quotes will fail the lookup.
   *      e.g. embedded variables are not supported.
   * @param {object} obj The object to check
   * @param {string} prop The property or property chain to get (e.g. obj.prop1.prop1a or obj['prop1'].prop2)
   * @returns {*|undefined} The value of the objects property or undefined if the property doesn't exist
   */
    getProp(obj, prop) {
        if (typeof obj !== 'object')
            throw 'getProp: obj is not an object';
        if (typeof prop !== 'string')
            throw 'getProp: prop is not a string';
        // Replace [] notation with dot notation
        prop = prop.replace(/\[["'`](.*)["'`]\]/g, '.$1');
        return prop.split('.').reduce(function (prev, curr) {
            return prev ? prev[curr] : undefined;
        }, obj || self);
    } // --- end of fn getProp() --- //
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: OrderByPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.1.1", ngImport: i0, type: OrderByPipe, isStandalone: true, name: "orderBy", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: OrderByPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'orderBy',
                    pure: false,
                    standalone: true,
                }]
        }] });

/**
 * Translated from AngularJS version from common-angular-components project:
 * https://bitbucket.os.liberty.edu/projects/CA/repos/common-angular-components/browse/app/components/phone-format/scripts/phone-format.js
 *
 * For better type-safety, the return type for missing values has been changed to an empty string.
 */
class SlPhoneFormatPipe {
    constructor() {
        this.PHONE_FORMAT = /^(\d{3})?(\d{3})(\d{4})$/;
    }
    transform(value) {
        if (!value) {
            return '';
        }
        const parsedValue = this.PHONE_FORMAT.exec(value);
        // if input isn't either 7 or 10 characters numeric, just return input
        return !parsedValue ? value : ((parsedValue[1]) ? '(' + parsedValue[1] + ') ' : '') + parsedValue[2] + '-' + parsedValue[3];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlPhoneFormatPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.1.1", ngImport: i0, type: SlPhoneFormatPipe, isStandalone: true, name: "phoneFormat" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlPhoneFormatPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'phoneFormat',
                    standalone: true,
                }]
        }] });

class ReversePipe {
    /* eslint-disable  @typescript-eslint/no-unsafe-return */
    transform(list) {
        return list.slice().reverse();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ReversePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.1.1", ngImport: i0, type: ReversePipe, isStandalone: true, name: "reverse" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: ReversePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'reverse',
                    standalone: true,
                }]
        }] });

/**
 * Applies the given validator when the given predicate returns true
 *
 * Adapted from the tutorial found here:
 * https://medium.com/ngx/3-ways-to-implement-conditional-validation-of-reactive-forms-c59ed6fc3325
 *
 * @param predicate
 * @param validator
 * @returns ValidatorFn
 */
function conditionalValidator(predicate, validator) {
    return ((formControl) => {
        if (!formControl.parent)
            return null;
        if (predicate())
            return validator(formControl);
        return null;
    });
}

class DateValidators {
    /**
     * Checks if the control date is after passed In timestamp
     * example ('01/01/2000')
     *
     * @static
     * @param {string} timestamp
     * @return {*}  {ValidatorFn}
     * @memberof DateValidators
     */
    static afterDate(timestamp, allowNull) {
        return (control) => {
            if (!control.value && allowNull) {
                return null;
            }
            // eslint-disable-next-line
            const chosenDate = new Date(control.value);
            // eslint-disable-next-line
            const pastDate = new Date(timestamp);
            // eslint-disable-next-line
            return chosenDate > pastDate ? null : { invalidPastDate: { value: control.value } };
        };
    }
    /**
     * Checks if the date is before the current date
     *
     * @static
     * @param {Date} date
     * @return {*}  {ValidatorFn}
     * @memberof DateValidators
     */
    static beforeCurrent(allowNull) {
        return (control) => {
            if (!control.value && allowNull) {
                return null;
            }
            // eslint-disable-next-line
            const chosenDate = new Date(control.value);
            const currentDate = new Date();
            // eslint-disable-next-line
            return !chosenDate || chosenDate < currentDate ? null : { invalidFutureDate: { value: control.value } };
        };
    }
}

class FileValidators {
    /**
     * Checks the size of a file against the given size (in MB)
     *
     * Adapted from https://blog.davidjs.com/2019/04/file-validators-in-angular/
     *
     * @static
     * @param {number} maxSizeInMB
     * @return {*}  {ValidatorFn}
     * @memberof FileValidators
     */
    static maxSize(maxSizeInMB) {
        return ((formControl) => {
            if (!formControl.value)
                return null;
            const file = formControl.value;
            const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
            if (file instanceof File && file.size > maxSizeInBytes) {
                return { maxSize: { requiredSize: maxSizeInBytes, actualSize: file.size, file } };
            }
            return null;
        });
    }
}

class SlMultiCheckComponent extends CVAListBase {
    constructor() {
        super();
        this.options = [];
        this.placeholder = '';
        this._value = [];
    }
    check(option) {
        if (!this.list.includes(option)) {
            this.add(option);
        }
        else {
            this.remove(option);
        }
    }
    isString(value) {
        return typeof value === 'string' && value !== null;
    }
    getNestedProperties(obj, keys) {
        if (this.isString(obj))
            return obj; // check if its actually an object
        if (!keys)
            return; // make sure optionKeys has a value
        return keys.reduce((acc, key) => {
            if (acc && key in acc) {
                return acc[key];
            }
            return undefined;
        }, obj);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlMultiCheckComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.1", type: SlMultiCheckComponent, isStandalone: true, selector: "sl-multi-check", inputs: { options: "options", label: "label", placeholder: "placeholder", optionKeys: "optionKeys", equals: ["compareWith", "equals"] }, host: { properties: { "class.hasError": "this.errorMessage" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SlMultiCheckComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<fieldset>\n    <legend>{{label}}</legend>\n    <ng-container *ngFor=\"let option of options\">\n        <label>\n            <input type=\"checkbox\" [checked]=\"list.includes(option)\" (change)=\"check(option)\" />\n            {{ getNestedProperties(option,optionKeys) }}\n        </label>\n    </ng-container>\n    <span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n        <ng-content select=\"sl-validation-error-message\"></ng-content>\n    </span>\n</fieldset>\n    \n", styles: [""], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: FormsModule }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.1", ngImport: i0, type: SlMultiCheckComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sl-multi-check', imports: [CommonModule, FormsModule], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SlMultiCheckComponent),
                            multi: true,
                        },
                    ], template: "<fieldset>\n    <legend>{{label}}</legend>\n    <ng-container *ngFor=\"let option of options\">\n        <label>\n            <input type=\"checkbox\" [checked]=\"list.includes(option)\" (change)=\"check(option)\" />\n            {{ getNestedProperties(option,optionKeys) }}\n        </label>\n    </ng-container>\n    <span *ngIf=\"errorMessage\" [id]=\"identifier+'-error'\">\n        <ng-content select=\"sl-validation-error-message\"></ng-content>\n    </span>\n</fieldset>\n    \n" }]
        }], ctorParameters: () => [], propDecorators: { errorMessage: [{
                type: HostBinding,
                args: ['class.hasError']
            }], options: [{
                type: Input
            }], label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], optionKeys: [{
                type: Input
            }], equals: [{
                type: Input,
                args: ['compareWith']
            }] } });

// Function to dynamically provide any service class
function provideImpersonate(config) {
    return {
        provide: 'IMPERSONATE_CONFIG',
        useValue: config
    };
}

/*
 * Public API Surface of sl-angular-library
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractUserService, CVABase, CVAListBase, DateValidators, EModalType, EMode, Editable, EditableList, FacultyStaffModes, FileSizePipe, FileValidators, FocusableDirective, FormDebuggerComponent, KeyboardNavigableDirective, ModeGroup, OrderByPipe, ReadOnlyHttpClient, ReversePipe, SlCheckboxComponent, SlDatetimeComponent, SlFormModule, SlIconComponent, SlImpersonateComponent, SlInputComponent, SlMenuComponent, SlModalComponent, SlModalService, SlMultiCheckComponent, SlMultiSelectComponent, SlPhoneFormatPipe, SlRadioComponent, SlTextareaComponent, SlTimeComponent, StudentStaffModes, TrapFocusDirective, ValidationErrorMessageComponent, ViewEditModes, ViewNewEditDeleteModes, ViewNewEditDeleteSearchModes, ViewNewEditModes, YesNoUnsureModes, canReceiveFocus, conditionalValidator, createUniqueIdentifier, customRequired, digitCheck, luidCheck, prepareString, provideImpersonate, withParams };
